import { createContext, useMemo, useEffect, useRef, useState, useCallback, useContext } from 'react';
import { UPLOAD_STEP_TIMEOUT_MS, buildBrowserClientInfo, VIDEO_READY_TIMEOUT_MS, ClockOffset, CAPTURE_FORMAT_CONTENT_TYPES, SCREENSHOT_INTERVAL_MS, CLIP_WEB_VIDEO_BITS_PER_SECOND, CLIP_WEBCODECS_QP, MAX_CLIP_FRAME_OVERRUN, JPEG_QUALITY, MAX_HEIGHT, MAX_WIDTH, MAX_CLIP_BYTES, CLIP_WEBCODECS_QP_MAX, CLIP_WEBCODECS_QP_STEP, CLIP_WEB_MIN_BITS_PER_SECOND, CLIP_FRAME_INTERVAL_MS, CLIP_FIRST_CUT_DELAY_MS, isCutAt, EDIT_HEARTBEAT_SECONDS, countCutUnits, MAX_PENDING_BUFFER, UPLOAD_RETRY_DELAYS_MS, MAX_UPLOAD_RETRIES, CANVAS_TO_BLOB_TIMEOUT_MS, MAX_CLIP_UPLOAD_FAILURES, nativeClipBitsPerSecond } from '@lookout/shared';
export { SESSION_STATUSES } from '@lookout/shared';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { ArrayBufferTarget, Muxer } from 'mp4-muxer';
import { motion, AnimatePresence } from 'motion/react';
import { Squircle } from '@squircle-js/react';
import NumberFlow from '@number-flow/react';
import { createPortal } from 'react-dom';
import { createPlayer } from '@videojs/react';
import { videoFeatures, VideoSkin, Video } from '@videojs/react/video';
import '@videojs/react/video/skin.css';
import { GearSixIcon, PlusIcon } from '@phosphor-icons/react';

// src/LookoutProvider.tsx
var HttpError = class extends Error {
  status;
  constructor(status, message) {
    super(message);
    this.name = "HttpError";
    this.status = status;
  }
};
async function resolveTokenValue(provider) {
  if (typeof provider === "string") return provider;
  const result = provider();
  return result instanceof Promise ? result : result;
}
function stepDeadline() {
  return typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function" ? AbortSignal.timeout(UPLOAD_STEP_TIMEOUT_MS) : void 0;
}
function isTimeout(err) {
  return err instanceof Error && (err.name === "TimeoutError" || err.name === "AbortError");
}
async function fetchJson(url, init) {
  const headers = {};
  if (init?.body) {
    headers["Content-Type"] = "application/json";
  }
  let res;
  try {
    res = await fetch(url, {
      signal: stepDeadline(),
      ...init,
      headers: { ...headers, ...init?.headers }
    });
  } catch (err) {
    if (isTimeout(err)) {
      throw new Error(
        `Timed out after ${UPLOAD_STEP_TIMEOUT_MS / 1e3}s fetching ${url}`
      );
    }
    const msg = err instanceof Error ? err.message : String(err);
    throw new Error(`Network error fetching ${url}: ${msg}`);
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let detail = "";
    try {
      const json = JSON.parse(text);
      detail = json.error || json.message || text;
    } catch {
      detail = text;
    }
    throw new HttpError(
      res.status,
      `HTTP ${res.status} ${res.statusText} from ${url}${detail ? "\n" + detail.slice(0, 500) : ""}`
    );
  }
  return res.json();
}
function createLookoutClient(options) {
  const { baseUrl, token, clientInfo } = options;
  const resolveToken = () => resolveTokenValue(token);
  async function sessionUrl(path = "") {
    const t = await resolveToken();
    return `${baseUrl}/api/sessions/${t}${path}`;
  }
  return {
    resolveToken,
    async getSession() {
      return fetchJson(await sessionUrl());
    },
    async getUploadUrl(opts) {
      const base = await sessionUrl("/upload-url");
      const params = new URLSearchParams();
      if (opts?.capturedAt) params.set("capturedAt", opts.capturedAt);
      if (opts?.format) params.set("format", opts.format);
      if (clientInfo) params.set("clientInfo", clientInfo);
      const qs = params.toString();
      return fetchJson(qs ? `${base}?${qs}` : base);
    },
    async confirmScreenshot(body) {
      return fetchJson(await sessionUrl("/screenshots"), {
        method: "POST",
        body: JSON.stringify(body)
      });
    },
    async uploadToR2(uploadUrl, blob, contentType = "image/jpeg") {
      if (!uploadUrl.startsWith("https://") && !uploadUrl.startsWith("/")) {
        throw new Error("Invalid upload URL: must be HTTPS or a relative path.");
      }
      let res;
      try {
        res = await fetch(uploadUrl, {
          method: "PUT",
          body: blob,
          // Must match the content type the presigned URL was signed with.
          headers: { "Content-Type": contentType },
          // The step most exposed to a weak uplink: this is the multi-MB
          // payload, and a stalled PUT used to hang the capture loop with
          // no error for it to fall back on.
          signal: stepDeadline()
        });
      } catch (err) {
        if (isTimeout(err)) {
          throw new Error(
            `R2 upload timed out after ${UPLOAD_STEP_TIMEOUT_MS / 1e3}s (${blob.size} bytes) \u2014 the connection stalled mid-transfer.`
          );
        }
        if (err instanceof TypeError) {
          throw new Error(
            "Upload failed: network error or CORS misconfiguration on R2 bucket."
          );
        }
        throw err;
      }
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        const code = /<Code>([^<]+)<\/Code>/.exec(text)?.[1] ?? "";
        const detail = /<Message>([^<]+)<\/Message>/.exec(text)?.[1] ?? "";
        const hint = code === "SignatureDoesNotMatch" ? " The request didn't match the presigned URL \u2014 something between the client and R2 is altering the request (a proxy, or a rewritten method/headers)." : code === "RequestTimeTooSkewed" ? " The signing server's clock is off relative to R2; fix NTP on the API server." : code === "AccessDenied" || /expire/i.test(detail) ? " The presigned URL had expired (they last ~2 minutes) or the credentials can't write this key. A slow upload of a large clip can outrun the expiry." : res.status === 403 && !text ? " Empty 403 body usually means CORS stripped the response: check the R2 bucket's CORS rules allow PUT from this origin." : "";
        console.error("[lookout] R2 upload failed", {
          status: res.status,
          code,
          detail,
          body: text.slice(0, 1e3)
        });
        throw new Error(
          `R2 upload failed: HTTP ${res.status}${code ? ` (${code})` : ""}${detail ? ` \u2014 ${detail}` : text ? " \u2014 " + text.slice(0, 200) : ""}${hint}`
        );
      }
    },
    async pause() {
      return fetchJson(await sessionUrl("/pause"), {
        method: "POST"
      });
    },
    async resume() {
      return fetchJson(await sessionUrl("/resume"), {
        method: "POST"
      });
    },
    async stop(opts) {
      return fetchJson(await sessionUrl("/stop"), {
        method: "POST",
        // Old servers ignore an unknown body; omit it entirely for the
        // plain stop so the request stays byte-identical to before.
        ...opts?.edit ? { body: JSON.stringify({ edit: true }) } : {}
      });
    },
    async rename(name) {
      return fetchJson(await sessionUrl("/name"), {
        method: "PATCH",
        body: JSON.stringify({ name })
      });
    },
    async getStatus() {
      return fetchJson(await sessionUrl("/status"));
    },
    async getVideo() {
      return fetchJson(await sessionUrl("/video"));
    },
    async getUnits() {
      return fetchJson(await sessionUrl("/units"));
    },
    async setCuts(cuts) {
      return fetchJson(await sessionUrl("/cuts"), {
        method: "PUT",
        body: JSON.stringify({ cuts })
      });
    },
    async applyCuts() {
      return fetchJson(await sessionUrl("/compile"), {
        method: "POST"
      });
    },
    async heartbeatEditing() {
      return fetchJson(await sessionUrl("/editing"), {
        method: "POST"
      });
    }
  };
}
function resolveConfig(config) {
  return {
    token: config.token,
    apiBaseUrl: config.apiBaseUrl ?? "",
    capture: {
      intervalMs: config.capture?.intervalMs ?? SCREENSHOT_INTERVAL_MS,
      jpegQuality: config.capture?.jpegQuality ?? JPEG_QUALITY,
      maxWidth: config.capture?.maxWidth ?? MAX_WIDTH,
      maxHeight: config.capture?.maxHeight ?? MAX_HEIGHT,
      displayMediaConstraints: config.capture?.displayMediaConstraints,
      mode: config.capture?.mode ?? "screen",
      camera: {
        deviceId: config.capture?.camera?.deviceId,
        userMediaConstraints: config.capture?.camera?.userMediaConstraints
      }
    },
    retry: {
      maxRetries: config.retry?.maxRetries ?? MAX_UPLOAD_RETRIES,
      retryDelays: config.retry?.retryDelays ?? UPLOAD_RETRY_DELAYS_MS,
      maxPendingBuffer: config.retry?.maxPendingBuffer ?? MAX_PENDING_BUFFER
    },
    callbacks: config.callbacks ?? {},
    statusPollIntervalMs: config.statusPollIntervalMs ?? 3e3,
    autoStart: config.autoStart ?? false
  };
}

// src/ui/theme.ts
if (typeof document !== "undefined" && !document.querySelector("style[data-lookout-theme]")) {
  const style = document.createElement("style");
  style.setAttribute("data-lookout-theme", "");
  style.textContent = `
    :root {
      /* Dark theme (default/fallback).

         These are Adwaita's surfaces \u2014 GNOME's own #242424 window over
         #1e1e1e views \u2014 used as the app's baseline on every platform, not
         just the one that ships them.

         Not pure black, and the reason matters: on macOS and Windows the
         window is usually wearing vibrancy or mica and this colour never
         shows, but those materials drop out (remote sessions, transparency
         disabled, an unsupported compositor) and the flat colour underneath
         is what the user is left with. #000 as that fallback reads as a hole
         in the screen rather than a surface. */
      --color-bg-body: #242424;
      --color-bg-panel: #1e1e1e;
      --color-modal-backdrop: rgba(0, 0, 0, 0.55);
      --color-bg-surface: rgba(255, 255, 255, 0.08);
      --color-bg-sunken: rgba(255, 255, 255, 0.04);
      --color-text-primary: #ffffff;
      --color-text-inverse: #000000;
      --color-text-secondary: rgba(255, 255, 255, 0.7);
      --color-text-tertiary: rgba(255, 255, 255, 0.5);
      --color-text-quaternary: rgba(255, 255, 255, 0.28);
      --color-text-error: #fca5a5;
      --color-border-default: rgba(255, 255, 255, 0.12);
      --color-border-hover: rgba(255, 255, 255, 0.22);
      --color-bg-selected: rgba(255, 255, 255, 0.12);
      --color-border-selected: rgba(255, 255, 255, 0.3);
      --color-icon-selected: rgba(255, 255, 255, 0.8);
      --color-status-neutral: rgba(255, 255, 255, 0.2);
      --color-spinner-base: rgba(255, 255, 255, 0.1);
      --color-spinner-track: rgba(255, 255, 255, 0.8);
      --color-skeleton-bg: rgba(255, 255, 255, 0.06);
      --color-skeleton-shimmer: rgba(255, 255, 255, 0.12);
      --color-badge-primary-bg: #22c55e26;
      --color-badge-primary-text: #22c55e;
      --color-badge-overlay-bg: rgba(0, 0, 0, 0.7);
      --color-badge-overlay-text: #ffffff;
      --color-archive-bg: rgba(0, 0, 0, 0.6);
      --color-archive-icon: #ffffff;
      --color-archive-border: rgba(255, 255, 255, 0.1);
      --color-archive-hover-bg: rgba(255, 255, 255, 0.1);
      --color-archive-hover-border: rgba(255, 255, 255, 0.2);
      /* Editor: a recessed well the footage sits in, and the removed-region
         vocabulary. Deliberately translucent so the window's vibrancy still
         reads through the chrome. */
      --color-well: rgba(0, 0, 0, 0.32);
      --color-well-border: rgba(255, 255, 255, 0.1);
      --color-cut-fill: rgba(248, 113, 113, 0.26);
      --color-cut-fill-hover: rgba(248, 113, 113, 0.36);
      --color-cut-border: #f87171;
      --color-cut-stripe: rgba(248, 113, 113, 0.13);
      --color-track: rgba(255, 255, 255, 0.1);
      /* Accent: the one colour an embedding program can replace. Drives
         primary buttons, focus rings, and progress. Semantic status
         colours (success/warning/danger) stay put \u2014 those carry meaning,
         not brand. */
      --color-accent: #3b82f6;
      --color-accent-hover: #2f6fd0;
      --color-accent-hover: color-mix(in oklab, var(--color-accent) 88%, black);
      --color-on-accent: #ffffff;
    }
    @media (prefers-color-scheme: light) {
      :root:not([data-theme="dark"]) {
        --color-bg-body: #fafafa;
        --color-bg-panel: #ffffff;
        --color-modal-backdrop: rgba(0, 0, 0, 0.35);
        --color-bg-surface: #ffffff;
        --color-bg-sunken: rgba(0, 0, 0, 0.04);
        --color-text-primary: rgba(0, 0, 0, 0.85);
        --color-text-inverse: #ffffff;
        --color-text-secondary: rgba(0, 0, 0, 0.65);
        --color-text-tertiary: rgba(0, 0, 0, 0.45);
        --color-text-quaternary: rgba(0, 0, 0, 0.25);
        --color-text-error: #ef4444;
        --color-border-default: rgba(0, 0, 0, 0.12);
        --color-border-hover: rgba(0, 0, 0, 0.22);
        --color-bg-selected: rgba(0, 0, 0, 0.1);
        --color-border-selected: rgba(0, 0, 0, 0.3);
        --color-icon-selected: rgba(0, 0, 0, 0.8);
        --color-status-neutral: #000000;
        --color-spinner-base: rgba(0, 0, 0, 0.1);
        --color-spinner-track: rgba(0, 0, 0, 0.8);
        --color-skeleton-bg: rgba(0, 0, 0, 0.05);
        --color-skeleton-shimmer: rgba(0, 0, 0, 0.08);
        --color-badge-primary-bg: #22c55e;
        --color-badge-primary-text: #ffffff;
        --color-badge-overlay-bg: #000000;
        --color-badge-overlay-text: #ffffff;
        --color-archive-bg: rgba(255, 255, 255, 0.9);
        --color-archive-icon: #000000;
        --color-archive-border: rgba(0, 0, 0, 0.1);
        --color-archive-hover-bg: rgba(255, 255, 255, 1);
        --color-archive-hover-border: rgba(0, 0, 0, 0.2);
        --color-well: rgba(0, 0, 0, 0.08);
        --color-well-border: rgba(0, 0, 0, 0.1);
        --color-cut-fill: rgba(220, 38, 38, 0.20);
        --color-cut-fill-hover: rgba(220, 38, 38, 0.30);
        --color-cut-border: #dc2626;
        --color-cut-stripe: rgba(220, 38, 38, 0.12);
        --color-track: rgba(0, 0, 0, 0.08);
        --color-accent: #3b82f6;
        --color-accent-hover: #2f6fd0;
      --color-accent-hover: color-mix(in oklab, var(--color-accent) 88%, black);
        --color-on-accent: #ffffff;
      }
    }
    :root[data-theme="light"] {
      --color-bg-body: #fafafa;
      --color-bg-panel: #ffffff;
      --color-modal-backdrop: rgba(0, 0, 0, 0.35);
      --color-bg-surface: #ffffff;
      --color-bg-sunken: rgba(0, 0, 0, 0.04);
      --color-text-primary: rgba(0, 0, 0, 0.85);
      --color-text-inverse: #ffffff;
      --color-text-secondary: rgba(0, 0, 0, 0.65);
      --color-text-tertiary: rgba(0, 0, 0, 0.45);
      --color-text-quaternary: rgba(0, 0, 0, 0.25);
      --color-text-error: #ef4444;
      --color-border-default: rgba(0, 0, 0, 0.12);
      --color-border-hover: rgba(0, 0, 0, 0.22);
      --color-bg-selected: rgba(0, 0, 0, 0.1);
      --color-border-selected: rgba(0, 0, 0, 0.3);
      --color-icon-selected: rgba(0, 0, 0, 0.8);
      --color-status-neutral: #000000;
      --color-spinner-base: rgba(0, 0, 0, 0.1);
      --color-spinner-track: rgba(0, 0, 0, 0.8);
      --color-skeleton-bg: rgba(0, 0, 0, 0.05);
      --color-skeleton-shimmer: rgba(0, 0, 0, 0.08);
      --color-badge-primary-bg: #22c55e;
      --color-badge-primary-text: #ffffff;
      --color-badge-overlay-bg: #000000;
      --color-badge-overlay-text: #ffffff;
      --color-archive-bg: rgba(255, 255, 255, 0.9);
      --color-archive-icon: #000000;
      --color-archive-border: rgba(0, 0, 0, 0.1);
      --color-archive-hover-bg: rgba(255, 255, 255, 1);
      --color-archive-hover-border: rgba(0, 0, 0, 0.2);
      --color-well: rgba(0, 0, 0, 0.08);
      --color-well-border: rgba(0, 0, 0, 0.1);
      --color-cut-fill: rgba(220, 38, 38, 0.20);
      --color-cut-fill-hover: rgba(220, 38, 38, 0.30);
      --color-cut-border: #dc2626;
      --color-cut-stripe: rgba(220, 38, 38, 0.12);
      --color-track: rgba(0, 0, 0, 0.08);
      --color-accent: #3b82f6;
      --color-accent-hover: #2f6fd0;
      --color-accent-hover: color-mix(in oklab, var(--color-accent) 88%, black);
      --color-on-accent: #ffffff;
    }`;
  document.head.appendChild(style);
}
var colors = {
  bg: { body: "var(--color-bg-body)", panel: "var(--color-bg-panel)", backdrop: "var(--color-modal-backdrop)", surface: "var(--color-bg-surface)", sunken: "var(--color-bg-sunken)", selected: "var(--color-bg-selected)" },
  text: { primary: "var(--color-text-primary)", inverse: "var(--color-text-inverse)", secondary: "var(--color-text-secondary)", tertiary: "var(--color-text-tertiary)", quaternary: "var(--color-text-quaternary)", error: "var(--color-text-error)" },
  border: { default: "var(--color-border-default)", hover: "var(--color-border-hover)", selected: "var(--color-border-selected)" },
  icon: { selected: "var(--color-icon-selected)" },
  spinner: { base: "var(--color-spinner-base)", track: "var(--color-spinner-track)" },
  /** The brand accent. Replaceable per-app via `<LookoutProvider
   *  accentColor>` or {@link setAccentColor}. */
  accent: {
    base: "var(--color-accent)",
    hover: "var(--color-accent-hover)",
    /** Text/icon colour that sits ON the accent. */
    on: "var(--color-on-accent)"
  },
  /** Editor surfaces: the recessed well footage sits in, the timeline
   *  track, and the removed-region vocabulary. */
  editor: {
    well: "var(--color-well)",
    wellBorder: "var(--color-well-border)",
    track: "var(--color-track)",
    cutFill: "var(--color-cut-fill)",
    cutFillHover: "var(--color-cut-fill-hover)",
    cutBorder: "var(--color-cut-border)",
    cutStripe: "var(--color-cut-stripe)"
  },
  skeleton: { bg: "var(--color-skeleton-bg)", shimmer: "var(--color-skeleton-shimmer)" },
  badge: {
    primaryBg: "var(--color-badge-primary-bg)",
    primaryText: "var(--color-badge-primary-text)",
    overlayBg: "var(--color-badge-overlay-bg)",
    overlayText: "var(--color-badge-overlay-text)"
  },
  status: {
    success: "#22c55e",
    info: "#3b82f6",
    warning: "#f59e0b",
    danger: "#ef4444",
    neutral: "var(--color-status-neutral)"
  }
};
var spacing = { xs: 4, sm: 8, md: 12, lg: 16, xl: 20, xxl: 24, xxxl: 32 };
var radii = { sm: 6, md: 8, lg: 10 };
var fontSize = { xs: 11, sm: 12, md: 13, lg: 14, xl: 16, xxl: 18, heading: 20, display: 24, timer: 32 };
var fontWeight = { normal: 400, medium: 500, semibold: 600, bold: 700 };
var statusConfig = {
  pending: { label: "Pending", color: colors.status.neutral },
  active: { label: "Recording", color: colors.status.success },
  paused: { label: "Paused", color: colors.status.warning },
  stopped: { label: "Processing", color: colors.status.info },
  compiling: { label: "Compiling", color: colors.status.info },
  complete: { label: "Complete", color: colors.status.success },
  failed: { label: "Failed", color: colors.status.danger }
};
function setAccentColor(accent, on) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (accent) root.style.setProperty("--color-accent", accent);
  else root.style.removeProperty("--color-accent");
  if (on) root.style.setProperty("--color-on-accent", on);
  else root.style.removeProperty("--color-on-accent");
}
var SDK_VERSION = "0.3.7" ;
var LookoutContext = createContext(null);
function useLookoutContext() {
  const ctx = useContext(LookoutContext);
  if (!ctx) {
    throw new Error(
      'Lookout hooks must be used within a <LookoutProvider>. Wrap your component tree with <LookoutProvider token="...">.'
    );
  }
  return ctx;
}
function LookoutProvider({
  children,
  accentColor,
  accentTextColor,
  ...config
}) {
  const resolved = useMemo(() => resolveConfig(config), [config]);
  useEffect(() => {
    if (!accentColor && !accentTextColor) return;
    setAccentColor(accentColor ?? null, accentTextColor ?? null);
    return () => setAccentColor(null, null);
  }, [accentColor, accentTextColor]);
  const clientInfo = useMemo(
    () => buildBrowserClientInfo({
      type: "sdk",
      version: SDK_VERSION,
      embeddedApp: config.appName
    }),
    [config.appName]
  );
  const client = useMemo(
    () => createLookoutClient({
      baseUrl: resolved.apiBaseUrl,
      token: resolved.token,
      clientInfo
    }),
    [resolved.apiBaseUrl, resolved.token, clientInfo]
  );
  const value = useMemo(() => ({ config: resolved, client }), [resolved, client]);
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.querySelector("style[data-lookout-keyframes]")) return;
    const style = document.createElement("style");
    style.setAttribute("data-lookout-keyframes", "");
    style.textContent = [
      "@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}",
      "@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}",
      "@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}"
    ].join("");
    document.head.appendChild(style);
  }, []);
  return /* @__PURE__ */ jsx(LookoutContext.Provider, { value, children });
}

// src/hooks/hqScale.ts
var scratch = [null, null];
function drawScaledHQ(src, srcW, srcH, target) {
  const ctx = target.getContext("2d");
  if (!ctx) return;
  let curSrc = src;
  let curW = srcW;
  let curH = srcH;
  let step = 0;
  while (curW / 2 >= target.width && curH / 2 >= target.height) {
    const nextW = Math.max(target.width, Math.floor(curW / 2));
    const nextH = Math.max(target.height, Math.floor(curH / 2));
    const dest = scratch[step % 2] ??= document.createElement("canvas");
    dest.width = nextW;
    dest.height = nextH;
    const dctx = dest.getContext("2d");
    if (!dctx) break;
    dctx.imageSmoothingEnabled = true;
    dctx.imageSmoothingQuality = "high";
    dctx.drawImage(curSrc, 0, 0, curW, curH, 0, 0, nextW, nextH);
    curSrc = dest;
    curW = nextW;
    curH = nextH;
    step++;
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(curSrc, 0, 0, curW, curH, 0, 0, target.width, target.height);
}

// src/hooks/captureUtils.ts
function waitForVideoReady(video, timeoutMs = VIDEO_READY_TIMEOUT_MS) {
  if (video.videoWidth > 0 && video.videoHeight > 0) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const deadline = Date.now() + timeoutMs;
    (function check() {
      if (video.videoWidth > 0 && video.videoHeight > 0) return resolve();
      if (Date.now() > deadline)
        return reject(new Error("Video not ready \u2014 no frames received"));
      requestAnimationFrame(check);
    })();
  });
}
function captureFrameAsJpeg(video, canvas, settings) {
  if (video.videoWidth === 0 || video.videoHeight === 0) {
    return Promise.resolve(null);
  }
  const scale = Math.min(
    settings.maxWidth / video.videoWidth,
    settings.maxHeight / video.videoHeight,
    1
  );
  canvas.width = Math.round(video.videoWidth * scale);
  canvas.height = Math.round(video.videoHeight * scale);
  if (!canvas.getContext("2d")) {
    return Promise.resolve(null);
  }
  drawScaledHQ(video, video.videoWidth, video.videoHeight, canvas);
  const capturedAtMs = Date.now();
  const toBlobPromise = new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        resolve(
          blob ? { blob, width: canvas.width, height: canvas.height, capturedAtMs } : null
        );
      },
      "image/jpeg",
      settings.jpegQuality
    );
  });
  const timeoutPromise = new Promise(
    (resolve) => setTimeout(() => resolve(null), CANVAS_TO_BLOB_TIMEOUT_MS)
  );
  return Promise.race([toBlobPromise, timeoutPromise]);
}

// src/hooks/useScreenCapture.ts
function useScreenCapture(overrides) {
  let settings;
  try {
    const { config } = useLookoutContext();
    settings = {
      ...config.capture,
      ...overrides
    };
  } catch {
    settings = {
      intervalMs: overrides?.intervalMs ?? 6e4,
      jpegQuality: overrides?.jpegQuality ?? 0.85,
      maxWidth: overrides?.maxWidth ?? 1920,
      maxHeight: overrides?.maxHeight ?? 1080,
      displayMediaConstraints: overrides?.displayMediaConstraints
    };
  }
  const streamRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [isSharing, setIsSharing] = useState(false);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const startSharing = useCallback(async () => {
    const s = settingsRef.current;
    const constraints = {
      video: {
        frameRate: { ideal: 1, max: 5 }
      },
      audio: false,
      ...s.displayMediaConstraints
    };
    let stream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia(constraints);
    } catch (err) {
      if (err instanceof TypeError) {
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: false
        });
      } else {
        throw err;
      }
    }
    streamRef.current = stream;
    const video = document.createElement("video");
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;
    await video.play();
    await waitForVideoReady(video);
    videoRef.current = video;
    if (!canvasRef.current) {
      canvasRef.current = document.createElement("canvas");
    }
    stream.getVideoTracks()[0].addEventListener("ended", () => {
      streamRef.current = null;
      setIsSharing(false);
    });
    setIsSharing(true);
  }, []);
  const takeScreenshot = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const s = settingsRef.current;
    if (!video || !canvas || !streamRef.current) {
      return Promise.resolve(null);
    }
    return captureFrameAsJpeg(video, canvas, s);
  }, []);
  const stopSharing = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setIsSharing(false);
  }, []);
  const getVideo = useCallback(() => videoRef.current, []);
  return { isSharing, startSharing, takeScreenshot, stopSharing, getVideo };
}
function useCameraCapture(overrides) {
  let settings;
  try {
    const { config } = useLookoutContext();
    settings = {
      maxWidth: overrides?.maxWidth ?? config.capture.maxWidth,
      maxHeight: overrides?.maxHeight ?? config.capture.maxHeight,
      jpegQuality: overrides?.jpegQuality ?? config.capture.jpegQuality,
      deviceId: overrides?.camera?.deviceId ?? config.capture.camera.deviceId,
      userMediaConstraints: overrides?.camera?.userMediaConstraints ?? config.capture.camera.userMediaConstraints
    };
  } catch {
    settings = {
      maxWidth: overrides?.maxWidth ?? 1920,
      maxHeight: overrides?.maxHeight ?? 1080,
      jpegQuality: overrides?.jpegQuality ?? 0.85,
      deviceId: overrides?.camera?.deviceId,
      userMediaConstraints: overrides?.camera?.userMediaConstraints
    };
  }
  const streamRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [isSharing, setIsSharing] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewStream, setPreviewStream] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(
    settings.deviceId ?? null
  );
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const enumerateDevices = useCallback(async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      const cameras = all.filter((d) => d.kind === "videoinput");
      setDevices(cameras);
      return cameras;
    } catch {
      return [];
    }
  }, []);
  useEffect(() => {
    enumerateDevices();
    const handler = () => enumerateDevices();
    navigator.mediaDevices.addEventListener("devicechange", handler);
    return () => navigator.mediaDevices.removeEventListener("devicechange", handler);
  }, [enumerateDevices]);
  const acquireStream = useCallback(
    async (deviceIdOverride) => {
      const s = settingsRef.current;
      const videoConstraints = {
        width: { ideal: s.maxWidth, max: s.maxWidth },
        height: { ideal: s.maxHeight, max: s.maxHeight },
        ...s.userMediaConstraints
      };
      const devId = deviceIdOverride ?? selectedDeviceId ?? s.deviceId;
      if (devId) {
        videoConstraints.deviceId = { exact: devId };
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints,
        audio: false
      });
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = stream;
      setPreviewStream(stream);
      const video = document.createElement("video");
      video.srcObject = stream;
      video.muted = true;
      video.playsInline = true;
      await video.play();
      await waitForVideoReady(video);
      videoRef.current = video;
      if (!canvasRef.current) {
        canvasRef.current = document.createElement("canvas");
      }
      const handleEnded = () => {
        streamRef.current = null;
        setPreviewStream(null);
        setIsPreviewing(false);
        setIsSharing(false);
      };
      stream.getVideoTracks()[0].addEventListener("ended", handleEnded);
      enumerateDevices();
      return stream;
    },
    [selectedDeviceId, enumerateDevices]
  );
  const startPreview = useCallback(async () => {
    await acquireStream();
    setIsPreviewing(true);
    setIsSharing(false);
  }, [acquireStream]);
  const stopPreview = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setPreviewStream(null);
    videoRef.current = null;
    setIsPreviewing(false);
    setIsSharing(false);
  }, []);
  const startSharing = useCallback(async () => {
    if (!streamRef.current) {
      await acquireStream();
    }
    setIsPreviewing(false);
    setIsSharing(true);
  }, [acquireStream]);
  const takeScreenshot = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const s = settingsRef.current;
    if (!video || !canvas || !streamRef.current) {
      return Promise.resolve(null);
    }
    return captureFrameAsJpeg(video, canvas, s);
  }, []);
  const stopSharing = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setPreviewStream(null);
    videoRef.current = null;
    setIsPreviewing(false);
    setIsSharing(false);
  }, []);
  const selectDevice = useCallback(
    async (deviceId) => {
      setSelectedDeviceId(deviceId);
      if (streamRef.current) {
        const wasSharing = isSharing;
        try {
          await acquireStream(deviceId);
          if (wasSharing) {
            setIsPreviewing(false);
            setIsSharing(true);
          } else {
            setIsPreviewing(true);
            setIsSharing(false);
          }
        } catch {
          streamRef.current = null;
          setPreviewStream(null);
          setIsPreviewing(false);
          setIsSharing(false);
        }
      }
    },
    [isSharing, acquireStream]
  );
  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);
  return {
    isSharing,
    startSharing,
    takeScreenshot,
    stopSharing,
    // Camera-specific:
    devices,
    selectedDeviceId,
    selectDevice,
    // Preview:
    isPreviewing,
    previewStream,
    startPreview,
    stopPreview
  };
}
var ENABLE_CREDIT_MODE = true;
async function retry(fn, maxRetries, delays) {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (err) {
      if (err instanceof HttpError && err.status === 409) throw err;
      if (i === maxRetries - 1) throw err;
      await new Promise((r) => setTimeout(r, delays[i] ?? delays[delays.length - 1]));
    }
  }
  throw new Error("Unreachable");
}
var ClipFormatRejectedError = class extends Error {
  granted;
  constructor(requested, granted) {
    super(
      `Server granted "${granted}" for a "${requested}" clip \u2014 switching to JPEG captures`
    );
    this.name = "ClipFormatRejectedError";
    this.granted = granted;
  }
};
function useUploader() {
  const { client, config } = useLookoutContext();
  const { maxRetries, retryDelays } = config.retry;
  const [uploads, setUploads] = useState({
    pending: 0,
    completed: 0,
    failed: 0
  });
  const [trackedSeconds, setTrackedSeconds] = useState(0);
  const [lastScreenshotUrl, setLastScreenshotUrl] = useState(null);
  const [lastError, setLastError] = useState(null);
  const [sessionConflict, setSessionConflict] = useState(false);
  const resetConflict = useCallback(() => setSessionConflict(false), []);
  const clockOffsetRef = useRef(new ClockOffset());
  const captureUploadConfirm = useCallback(
    async (capture) => {
      setUploads((s) => ({ ...s, pending: s.pending + 1 }));
      try {
        const localCapturedAtMs = capture.capturedAtMs ?? Date.now();
        const capturedAt = ENABLE_CREDIT_MODE ? new Date(clockOffsetRef.current.correct(localCapturedAtMs)).toISOString() : void 0;
        const format = capture.format ?? "jpeg";
        const sentAt = Date.now();
        const urlResponse = await retry(
          () => client.getUploadUrl({
            capturedAt,
            format: format === "jpeg" ? void 0 : format
          }),
          maxRetries,
          retryDelays
        );
        if (urlResponse.serverTime) {
          clockOffsetRef.current.observe(
            urlResponse.serverTime,
            sentAt,
            Date.now()
          );
          if (urlResponse.capturedAtAdopted) {
            console.warn(
              `[lookout] this device's clock is ~${Math.round(
                clockOffsetRef.current.offset / 1e3
              )}s off from the server, so that capture was stamped on arrival. Later captures are corrected automatically.`
            );
          }
        }
        const { uploadUrl, screenshotId } = urlResponse;
        if (format !== "jpeg" && urlResponse.format !== format) {
          throw new ClipFormatRejectedError(
            format,
            urlResponse.format ?? "jpeg"
          );
        }
        await retry(
          () => client.uploadToR2(
            uploadUrl,
            capture.blob,
            CAPTURE_FORMAT_CONTENT_TYPES[format]
          ),
          maxRetries,
          retryDelays
        );
        const result = await retry(
          () => client.confirmScreenshot({
            screenshotId,
            width: capture.width,
            height: capture.height,
            fileSize: capture.blob.size,
            ...capture.frameCount ? { frameCount: capture.frameCount } : {}
          }),
          maxRetries,
          retryDelays
        );
        setTrackedSeconds(result.trackedSeconds);
        const previewBlob = format === "jpeg" ? capture.blob : capture.previewBlob ?? null;
        if (previewBlob) {
          setLastScreenshotUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return URL.createObjectURL(previewBlob);
          });
        }
        setUploads((s) => ({
          ...s,
          pending: s.pending - 1,
          completed: s.completed + 1
        }));
        config.callbacks.onUploadSuccess?.({
          screenshotId,
          trackedSeconds: result.trackedSeconds
        });
        return {
          trackedSeconds: result.trackedSeconds,
          nextExpectedAt: result.nextExpectedAt
        };
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Upload failed";
        setLastError(msg);
        setUploads((s) => ({
          ...s,
          pending: s.pending - 1,
          failed: s.failed + 1
        }));
        config.callbacks.onUploadFailure?.(err instanceof Error ? err : new Error(msg));
        if (err instanceof HttpError && err.status === 409) {
          setSessionConflict(true);
        }
        throw err;
      }
    },
    [client, maxRetries, retryDelays, config.callbacks]
  );
  return {
    captureUploadConfirm,
    uploads,
    trackedSeconds,
    lastScreenshotUrl,
    lastError,
    sessionConflict,
    resetConflict
  };
}
function useSession() {
  const { client, config } = useLookoutContext();
  const pollIntervalMs = config.statusPollIntervalMs;
  const [state, setState] = useState({
    status: "loading",
    name: "",
    trackedSeconds: 0,
    screenshotCount: 0,
    startedAt: null,
    createdAt: null,
    totalActiveSeconds: 0,
    clipsEnabled: false,
    frameIntervalMs: null,
    error: null
  });
  const pollRef = useRef(null);
  const loadSession = useCallback(async () => {
    let token;
    try {
      token = await client.resolveToken();
    } catch {
      setState((s) => ({ ...s, status: "no-token" }));
      return;
    }
    if (!token) {
      setState((s) => ({ ...s, status: "no-token" }));
      return;
    }
    try {
      const data = await client.getSession();
      setState({
        status: data.status,
        name: data.name,
        trackedSeconds: data.trackedSeconds,
        screenshotCount: data.screenshotCount,
        startedAt: data.startedAt,
        createdAt: data.createdAt,
        totalActiveSeconds: data.totalActiveSeconds,
        clipsEnabled: data.clipsEnabled === true,
        frameIntervalMs: data.frameIntervalMs ?? null,
        error: null
      });
      if (data.status === "compiling" || data.status === "stopped") {
        startPolling();
      }
    } catch (err) {
      setState((s) => ({
        ...s,
        status: "error",
        error: err instanceof Error ? err.message : "Unknown error"
      }));
    }
  }, [client]);
  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const data = await client.getStatus();
        setState((s) => ({
          ...s,
          status: data.status,
          trackedSeconds: data.trackedSeconds
        }));
        if (data.status === "complete" || data.status === "failed") {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (e) {
        console.warn("[session] poll error:", e);
      }
    }, pollIntervalMs);
  }, [client, pollIntervalMs]);
  const syncStatus = useCallback(async () => {
    try {
      const data = await client.getStatus();
      setState((s) => ({ ...s, status: data.status, trackedSeconds: data.trackedSeconds }));
    } catch {
    }
  }, [client]);
  const pause = useCallback(async () => {
    try {
      const data = await client.pause();
      setState((s) => ({
        ...s,
        status: data.status,
        totalActiveSeconds: data.totalActiveSeconds
      }));
    } catch (e) {
      if (e instanceof HttpError && e.status === 409) {
        console.warn("[session] pause returned 409, syncing status");
        await syncStatus();
      } else {
        throw e;
      }
    }
  }, [client, syncStatus]);
  const resume = useCallback(async () => {
    try {
      const data = await client.resume();
      setState((s) => ({ ...s, status: data.status }));
    } catch (e) {
      if (e instanceof HttpError && e.status === 409) {
        console.warn("[session] resume returned 409, syncing status");
        await syncStatus();
      } else {
        throw e;
      }
    }
  }, [client, syncStatus]);
  const stop = useCallback(async (name, opts) => {
    if (name) {
      try {
        await client.rename(name);
      } catch (e) {
        console.warn("[session] rename failed (non-fatal):", e);
      }
    }
    let stopped = false;
    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [1e3, 2e3, 4e3];
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const data = await client.stop(opts);
        setState((s) => ({
          ...s,
          status: data.status,
          trackedSeconds: data.trackedSeconds,
          totalActiveSeconds: data.totalActiveSeconds
        }));
        stopped = true;
        break;
      } catch (e) {
        if (e instanceof HttpError && e.status === 409) {
          console.warn("[session] stop returned 409, syncing status");
          const data = await client.getStatus();
          setState((s) => ({ ...s, status: data.status, trackedSeconds: data.trackedSeconds }));
          stopped = data.status === "stopped" || data.status === "compiling";
          break;
        }
        const isRetryable = e instanceof HttpError && e.status >= 500;
        if (isRetryable && attempt < MAX_RETRIES) {
          console.warn(`[session] stop failed (${e.status}), retrying in ${RETRY_DELAYS[attempt]}ms (attempt ${attempt + 1}/${MAX_RETRIES})`);
          await new Promise((r) => setTimeout(r, RETRY_DELAYS[attempt]));
          continue;
        }
        throw e;
      }
    }
    if (stopped) {
      startPolling();
    }
  }, [client, startPolling]);
  const updateTrackedSeconds = useCallback((seconds) => {
    setState((s) => ({ ...s, trackedSeconds: seconds }));
  }, []);
  const setError = useCallback((error) => {
    setState((s) => ({ ...s, error, ...error ? { status: "error" } : {} }));
  }, []);
  useEffect(() => {
    loadSession();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [loadSession]);
  return {
    ...state,
    pause,
    resume,
    stop,
    reload: loadSession,
    syncStatus,
    updateTrackedSeconds,
    setError
  };
}
var MAX_INTERPOLATION_S = Math.floor(SCREENSHOT_INTERVAL_MS / 1e3);
function deriveDisplaySeconds(baseSeconds, anchorAt, isActive, now) {
  if (!isActive) return baseSeconds;
  const elapsed = Math.floor((now - anchorAt) / 1e3);
  return baseSeconds + Math.min(MAX_INTERPOLATION_S, Math.max(0, elapsed));
}
function useSessionTimerState(serverTrackedSeconds, isActive) {
  const [displaySeconds, setDisplaySeconds] = useState(serverTrackedSeconds);
  const lastSyncRef = useRef(Date.now());
  const baseRef = useRef(serverTrackedSeconds);
  useEffect(() => {
    const newBase = Math.max(baseRef.current, serverTrackedSeconds);
    if (newBase !== baseRef.current) {
      baseRef.current = newBase;
      setDisplaySeconds(newBase);
      lastSyncRef.current = Date.now();
    }
  }, [serverTrackedSeconds]);
  useEffect(() => {
    if (!isActive) {
      setDisplaySeconds(baseRef.current);
      return;
    }
    lastSyncRef.current = Date.now();
    let raf;
    let lastRendered = -1;
    const tick = () => {
      const next = deriveDisplaySeconds(
        baseRef.current,
        lastSyncRef.current,
        true,
        Date.now()
      );
      if (next !== lastRendered) {
        lastRendered = next;
        setDisplaySeconds(next);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
    };
  }, [isActive]);
  return {
    displaySeconds,
    baseSeconds: baseRef.current,
    anchorAt: lastSyncRef.current
  };
}
function useSessionTimer(serverTrackedSeconds, isActive) {
  return useSessionTimerState(serverTrackedSeconds, isActive).displaySeconds;
}
function formatTime(totalSeconds) {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor(totalSeconds % 3600 / 60);
  const s = totalSeconds % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${m}:${String(s).padStart(2, "0")}`;
}
function formatTrackedTime(totalSeconds) {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor(totalSeconds % 3600 / 60);
  if (h > 0 && m > 0) return `${h}h ${m}min`;
  if (h > 0) return `${h}h`;
  if (m > 0) return `${m}min`;
  return "< 1min";
}
function useSilentAudioKeepAlive(enabled) {
  const contextRef = useRef(null);
  const oscillatorRef = useRef(null);
  useEffect(() => {
    if (!enabled) return;
    const Ctor = typeof window === "undefined" ? void 0 : window.AudioContext ?? window.webkitAudioContext;
    if (!Ctor) return;
    let ctx;
    try {
      ctx = new Ctor();
    } catch {
      return;
    }
    contextRef.current = ctx;
    try {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      gain.gain.value = 0;
      oscillator.connect(gain).connect(ctx.destination);
      oscillator.start();
      oscillatorRef.current = oscillator;
    } catch {
    }
    return () => {
      try {
        oscillatorRef.current?.stop();
      } catch {
      }
      oscillatorRef.current = null;
      contextRef.current?.close().catch(() => {
      });
      contextRef.current = null;
    };
  }, [enabled]);
}

// src/hooks/computeBestTracked.ts
function computeBestTrackedSeconds(inputs) {
  return Math.max(inputs.sessionTrackedSeconds, inputs.uploaderTrackedSeconds);
}
function avcCodecCandidates(width, height) {
  const level = width * height > 1920 * 1088 ? "33" : "28";
  return [`avc1.6400${level}`, `avc1.4d00${level}`, `avc1.4200${level}`];
}
var configCache = /* @__PURE__ */ new Map();
async function resolveConfig2(width, height, frameIntervalMs) {
  const base = {
    width,
    height,
    // Integer fps hint, exactly as the desktop passes to VideoToolbox —
    // any cadence at or above one second collapses to 1.
    framerate: Math.max(1, Math.round(1e3 / Math.max(1, frameIntervalMs))),
    avc: { format: "avc" }
    // avcC bitstream — what MP4 carries
  };
  for (const codec of avcCodecCandidates(width, height)) {
    const candidates = [
      {
        config: {
          ...base,
          codec,
          latencyMode: "quality",
          bitrateMode: "quantizer"
        },
        quantizerMode: true
      },
      // VBR with real per-frame timestamps — the desktop formula verbatim
      // (bits per second of MEDIA time; see nativeClipBitsPerSecond).
      // "realtime" mirrors the desktop's VideoToolbox input mode AND
      // guarantees no B-frame reordering, which the MP4 muxing here does
      // not handle (chunks are laid down in arrival order).
      {
        config: {
          ...base,
          codec,
          latencyMode: "realtime",
          bitrate: nativeClipBitsPerSecond(frameIntervalMs),
          bitrateMode: "variable"
        },
        quantizerMode: false
      }
    ];
    for (const candidate of candidates) {
      try {
        const support = await VideoEncoder.isConfigSupported(candidate.config);
        if (support.supported) return candidate;
      } catch {
      }
    }
  }
  return null;
}
var WebCodecsClipEngine = class {
  constructor(width, height, frameIntervalMs, quantizer) {
    this.width = width;
    this.height = height;
    this.frameIntervalMs = frameIntervalMs;
    this.quantizer = quantizer;
    this.ready = this.init();
  }
  width;
  height;
  frameIntervalMs;
  quantizer;
  kind = "webcodecs";
  /** Set on any unrecoverable error. The recorder reads this to decide
   *  whether to stop trying WebCodecs for the rest of the session. */
  failed = false;
  encoder = null;
  chunks = [];
  /** Frames drawn before the async config probe resolved. The probe takes
   *  microtasks, frames arrive seconds apart — this should never hold more
   *  than the opening frame. Bounded anyway: a stuck probe must not pin
   *  dozens of raw 1080p frames in memory. */
  pendingFrames = [];
  ready;
  quantizerMode = false;
  /** No new frames accepted (finish() or cancel() was called). */
  done = false;
  /** Deliberately discarded — init must not keep building the encoder. */
  cancelled = false;
  static isSupported() {
    return typeof VideoEncoder !== "undefined" && typeof VideoFrame !== "undefined" && typeof VideoEncoder.isConfigSupported === "function";
  }
  async init() {
    try {
      const key = `${this.width}x${this.height}@${this.frameIntervalMs}`;
      let resolved = configCache.get(key);
      if (!resolved) {
        resolved = resolveConfig2(this.width, this.height, this.frameIntervalMs);
        configCache.set(key, resolved);
      }
      const picked = await resolved;
      if (this.cancelled) return;
      if (!picked) {
        configCache.delete(key);
        this.fail("no supported H.264 encoder config");
        return;
      }
      this.quantizerMode = picked.quantizerMode;
      this.encoder = new VideoEncoder({
        output: (chunk, meta) => {
          this.chunks.push({ chunk, meta });
        },
        error: (err) => {
          this.fail(`encoder error: ${err?.message ?? err}`);
        }
      });
      this.encoder.configure(picked.config);
      const pending = this.pendingFrames;
      this.pendingFrames = [];
      for (const { frame, keyFrame } of pending) {
        this.encodeFrame(frame, keyFrame);
      }
    } catch (err) {
      this.fail(`encoder init failed: ${err}`);
    }
  }
  fail(why) {
    if (!this.failed) {
      this.failed = true;
      console.warn(`[lookout] WebCodecs clip engine: ${why}`);
    }
    this.discardPending();
    if (this.encoder) {
      try {
        this.encoder.close();
      } catch {
      }
      this.encoder = null;
    }
  }
  discardPending() {
    for (const { frame } of this.pendingFrames) {
      try {
        frame.close();
      } catch {
      }
    }
    this.pendingFrames = [];
  }
  encodeFrame(frame, keyFrame) {
    const encoder = this.encoder;
    if (!encoder) {
      frame.close();
      return;
    }
    try {
      const opts = { keyFrame };
      if (this.quantizerMode) {
        opts.avc = { quantizer: this.quantizer };
      }
      encoder.encode(frame, opts);
    } catch (err) {
      this.fail(`encode failed: ${err}`);
    } finally {
      try {
        frame.close();
      } catch {
      }
    }
  }
  addFrame(canvas, ptsMs, keyFrame) {
    if (this.failed || this.done) return;
    let frame;
    try {
      frame = new VideoFrame(canvas, {
        timestamp: ptsMs * 1e3,
        // microseconds
        duration: this.frameIntervalMs * 1e3
      });
    } catch (err) {
      this.fail(`VideoFrame from canvas failed: ${err}`);
      return;
    }
    if (!this.encoder) {
      if (this.pendingFrames.length >= 4) {
        frame.close();
        this.fail("config probe never resolved");
        return;
      }
      this.pendingFrames.push({ frame, keyFrame });
      return;
    }
    this.encodeFrame(frame, keyFrame);
  }
  async finish() {
    this.done = true;
    await this.ready;
    this.discardPending();
    const encoder = this.encoder;
    if (this.failed || !encoder) return null;
    try {
      await encoder.flush();
    } catch (err) {
      this.fail(`flush failed: ${err}`);
      return null;
    }
    try {
      encoder.close();
    } catch {
    }
    this.encoder = null;
    if (this.chunks.length === 0) return null;
    for (let i = 1; i < this.chunks.length; i++) {
      if (this.chunks[i].chunk.timestamp <= this.chunks[i - 1].chunk.timestamp) {
        this.fail("encoder emitted reordered frames");
        return null;
      }
    }
    try {
      const target = new ArrayBufferTarget();
      const muxer = new Muxer({
        target,
        video: { codec: "avc", width: this.width, height: this.height },
        // Whole clip is in memory anyway; a front-loaded moov lets the
        // worker (and any preview player) read it in one pass.
        fastStart: "in-memory"
      });
      for (const { chunk, meta } of this.chunks) {
        muxer.addVideoChunk(chunk, meta);
      }
      muxer.finalize();
      return {
        blob: new Blob([target.buffer], { type: "video/mp4" }),
        format: "mp4"
      };
    } catch (err) {
      this.fail(`mp4 mux failed: ${err}`);
      return null;
    } finally {
      this.chunks = [];
    }
  }
  cancel() {
    this.done = true;
    this.cancelled = true;
    this.discardPending();
    if (this.encoder) {
      try {
        this.encoder.close();
      } catch {
      }
      this.encoder = null;
    }
    this.chunks = [];
  }
};

// src/hooks/clipRecorder.ts
var MIME_CANDIDATES = [
  { mime: "video/mp4;codecs=avc1.640028", format: "mp4" },
  // H.264 High 4.0
  { mime: "video/mp4;codecs=avc1.4d0028", format: "mp4" },
  // H.264 Main 4.0
  { mime: "video/mp4;codecs=avc1.42e01e", format: "mp4" },
  // H.264 Baseline 3.0
  { mime: "video/mp4", format: "mp4" },
  { mime: "video/webm;codecs=vp9", format: "webm" },
  { mime: "video/webm;codecs=vp8", format: "webm" },
  { mime: "video/webm", format: "webm" }
];
function pickMimeCandidate() {
  if (typeof MediaRecorder === "undefined") return null;
  for (const c of MIME_CANDIDATES) {
    try {
      if (MediaRecorder.isTypeSupported(c.mime)) return c;
    } catch {
    }
  }
  return null;
}
function canvasCaptureSupported() {
  return typeof HTMLCanvasElement !== "undefined" && typeof HTMLCanvasElement.prototype.captureStream === "function";
}
var MediaRecorderClipEngine = class {
  kind = "mediarecorder";
  stream;
  recorder;
  parts = [];
  format;
  contentType;
  constructor(canvas, mime, bitrate) {
    let stream;
    try {
      stream = canvas.captureStream(0);
      if (!this.streamHasRequestFrame(stream)) {
        stream.getTracks().forEach((t) => t.stop());
        stream = canvas.captureStream();
      }
    } catch {
      stream = canvas.captureStream();
    }
    this.stream = stream;
    this.format = mime.format;
    this.contentType = mime.mime.split(";")[0];
    this.recorder = new MediaRecorder(stream, {
      mimeType: mime.mime,
      videoBitsPerSecond: bitrate
    });
    this.recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.parts.push(e.data);
    };
    this.recorder.start();
  }
  streamHasRequestFrame(stream) {
    const track = stream.getVideoTracks()[0];
    const s = stream;
    return typeof track?.requestFrame === "function" || typeof s.requestFrame === "function";
  }
  addFrame() {
    const track = this.stream.getVideoTracks()[0];
    if (typeof track?.requestFrame === "function") {
      track.requestFrame();
    } else {
      this.stream.requestFrame?.();
    }
  }
  async finish() {
    const recorder = this.recorder;
    const stopped = new Promise((resolve) => {
      recorder.onstop = () => resolve();
      recorder.onerror = () => resolve();
    });
    try {
      recorder.stop();
    } catch {
    }
    await Promise.race([
      stopped,
      new Promise((resolve) => setTimeout(resolve, 1e4))
    ]);
    this.stream.getTracks().forEach((t) => t.stop());
    if (this.parts.length === 0) return null;
    return {
      blob: new Blob(this.parts, { type: this.contentType }),
      format: this.format
    };
  }
  cancel() {
    try {
      if (this.recorder.state !== "inactive") this.recorder.stop();
    } catch {
    }
    this.stream.getTracks().forEach((t) => t.stop());
    this.parts = [];
  }
};
var ClipRecorder = class _ClipRecorder {
  /** Session-wide latch: once the WebCodecs engine fails, stop offering it.
   *  Static so every recorder (and every restart) sees the verdict. */
  static webCodecsDisabled = false;
  video;
  frameIntervalMs;
  openingFrameIntervalMs;
  canvas = null;
  engine = null;
  frameCount = 0;
  frameTimer = null;
  mime;
  /** Live MediaRecorder-engine bitrate. Starts at the measured-optimal web
   *  rate and halves whenever a finished clip overruns MAX_CLIP_BYTES — see
   *  `cut()`. Browsers whose rate control does honour real frame spacing
   *  would otherwise blow the cap on every single clip. */
  bitrate = CLIP_WEB_VIDEO_BITS_PER_SECOND;
  /** Live WebCodecs-engine quantizer (H.264 QP). Coarsens when a finished
   *  clip overruns MAX_CLIP_BYTES — the constant-quality analogue of the
   *  bitrate backoff above. */
  quantizer = CLIP_WEBCODECS_QP;
  /** Hard frame cap for one clip — see MAX_CLIP_FRAME_OVERRUN. Derived from
   *  the SERVER's cadence, not the default constant, so a server that
   *  dictates a different frameIntervalMs still gets a correct cap. */
  maxFrames;
  // Opening cadence lives in its own field (cleared after the first cut),
  // so it's excluded from the always-resolved options.
  opts;
  static isSupported() {
    return WebCodecsClipEngine.isSupported() || canvasCaptureSupported() && pickMimeCandidate() !== null;
  }
  constructor(video, frameIntervalMs, opts) {
    const mime = pickMimeCandidate();
    if (!mime && !WebCodecsClipEngine.isSupported()) {
      throw new Error("Clip recording not supported in this browser");
    }
    this.video = video;
    this.frameIntervalMs = frameIntervalMs;
    this.openingFrameIntervalMs = opts?.openingFrameIntervalMs ?? null;
    this.maxFrames = Math.ceil(SCREENSHOT_INTERVAL_MS / Math.max(1, frameIntervalMs)) * MAX_CLIP_FRAME_OVERRUN;
    this.mime = mime;
    this.opts = {
      maxWidth: opts?.maxWidth ?? MAX_WIDTH,
      maxHeight: opts?.maxHeight ?? MAX_HEIGHT,
      jpegQuality: opts?.jpegQuality ?? JPEG_QUALITY
    };
  }
  /** Start recording a fresh clip. No-op if already recording. */
  start() {
    if (this.engine) return;
    if (this.video.videoWidth === 0 || this.video.videoHeight === 0) {
      throw new Error("Video not ready \u2014 cannot start clip recorder");
    }
    const scale = Math.min(
      this.opts.maxWidth / this.video.videoWidth,
      this.opts.maxHeight / this.video.videoHeight,
      1
    );
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(2, Math.round(this.video.videoWidth * scale / 2) * 2);
    canvas.height = Math.max(2, Math.round(this.video.videoHeight * scale / 2) * 2);
    this.canvas = canvas;
    this.frameCount = 0;
    if (!_ClipRecorder.webCodecsDisabled && WebCodecsClipEngine.isSupported()) {
      this.engine = new WebCodecsClipEngine(
        canvas.width,
        canvas.height,
        this.frameIntervalMs,
        this.quantizer
      );
    } else if (this.mime && canvasCaptureSupported()) {
      this.engine = new MediaRecorderClipEngine(canvas, this.mime, this.bitrate);
    } else {
      this.canvas = null;
      throw new Error("No clip encoder available");
    }
    this.drawFrame();
    const cadence = this.openingFrameIntervalMs ?? this.frameIntervalMs;
    this.frameTimer = setInterval(() => this.drawFrame(), cadence);
  }
  drawFrame() {
    const canvas = this.canvas;
    if (!canvas || this.video.videoWidth === 0 || this.video.videoHeight === 0)
      return;
    if (this.frameCount >= this.maxFrames) {
      if (this.frameTimer) {
        clearInterval(this.frameTimer);
        this.frameTimer = null;
      }
      return;
    }
    drawScaledHQ(this.video, this.video.videoWidth, this.video.videoHeight, canvas);
    this.engine?.addFrame(
      canvas,
      this.frameCount * this.frameIntervalMs,
      this.frameCount === 0
    );
    this.frameCount++;
  }
  /**
   * Finalize the current clip and immediately start the next one.
   * Returns null when the clip is unusable (no frames / empty blob) —
   * callers should fall back to a single-JPEG capture for that tick.
   */
  async cut() {
    const engine = this.engine;
    const canvas = this.canvas;
    if (!engine || !canvas) return null;
    this.drawFrame();
    const capturedAtMs = Date.now();
    const frameCount = this.frameCount;
    const truncated = frameCount >= this.maxFrames;
    if (this.frameTimer) {
      clearInterval(this.frameTimer);
      this.frameTimer = null;
    }
    const TIMED_OUT = /* @__PURE__ */ Symbol("finish timeout");
    let out = await Promise.race([
      engine.finish(),
      new Promise(
        (resolve) => setTimeout(() => resolve(TIMED_OUT), 1e4)
      )
    ]);
    if (out === TIMED_OUT) {
      engine.cancel();
      out = null;
    }
    const width = canvas.width;
    const height = canvas.height;
    const previewBlob = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/jpeg", this.opts.jpegQuality);
      setTimeout(() => resolve(null), 5e3);
    });
    if (!out && frameCount > 0 && engine.kind === "webcodecs") {
      _ClipRecorder.webCodecsDisabled = true;
      console.warn(
        "[lookout] WebCodecs clip engine produced no output \u2014 falling back to MediaRecorder for the rest of this session."
      );
    }
    const blob = out && frameCount > 0 && out.blob.size > 0 ? out.blob : null;
    let oversize = false;
    if (blob && blob.size > MAX_CLIP_BYTES) {
      oversize = true;
      if (truncated) {
        console.warn(
          `[lookout] clip was ${blob.size} bytes (cap ${MAX_CLIP_BYTES}) after running long on a slow upload \u2014 keeping encoder settings`
        );
      } else if (engine.kind === "webcodecs") {
        const coarser = Math.min(
          CLIP_WEBCODECS_QP_MAX,
          this.quantizer + CLIP_WEBCODECS_QP_STEP
        );
        if (coarser !== this.quantizer) {
          console.warn(
            `[lookout] clip was ${blob.size} bytes (cap ${MAX_CLIP_BYTES}) \u2014 coarsening encoder QP ${this.quantizer} -> ${coarser}`
          );
          this.quantizer = coarser;
        }
      } else {
        const reduced = Math.max(
          CLIP_WEB_MIN_BITS_PER_SECOND,
          Math.round(this.bitrate / 2)
        );
        if (reduced !== this.bitrate) {
          console.warn(
            `[lookout] clip was ${blob.size} bytes (cap ${MAX_CLIP_BYTES}) \u2014 dropping encoder bitrate ${this.bitrate} -> ${reduced}`
          );
          this.bitrate = reduced;
        }
      }
    }
    this.openingFrameIntervalMs = null;
    this.teardown();
    try {
      this.start();
    } catch {
    }
    if (!blob || !out || oversize) return null;
    return {
      blob,
      format: out.format,
      width,
      height,
      frameCount,
      truncated,
      capturedAtMs,
      previewBlob
    };
  }
  /** Stop and discard the in-progress clip (pause/stop/unmount). */
  stop() {
    this.engine?.cancel();
    this.teardown();
  }
  teardown() {
    if (this.frameTimer) {
      clearInterval(this.frameTimer);
      this.frameTimer = null;
    }
    this.engine = null;
    this.canvas = null;
    this.frameCount = 0;
  }
};

// src/hooks/useLookout.ts
function useLookout() {
  const { config, client } = useLookoutContext();
  const callbacksRef = useRef(config.callbacks);
  callbacksRef.current = config.callbacks;
  const captureMode = config.capture.mode;
  const session = useSession();
  const screenCapture = useScreenCapture();
  const cameraCapture = useCameraCapture();
  const capture = captureMode === "camera" ? cameraCapture : screenCapture;
  const uploader = useUploader();
  const bestTrackedSeconds = computeBestTrackedSeconds({
    sessionTrackedSeconds: session.trackedSeconds,
    uploaderTrackedSeconds: uploader.trackedSeconds
  });
  const displaySeconds = useSessionTimer(
    bestTrackedSeconds,
    capture.isSharing && (session.status === "active" || session.status === "pending")
  );
  const intervalRef = useRef(null);
  const clipRecorderRef = useRef(null);
  const capturingRef = useRef(false);
  const prevStatusRef = useRef(session.status);
  const intentionalPauseRef = useRef(false);
  const pauseInFlightRef = useRef(false);
  const resumeInFlightRef = useRef(false);
  const stopInFlightRef = useRef(false);
  useEffect(() => {
    if (bestTrackedSeconds > session.trackedSeconds) {
      session.updateTrackedSeconds(bestTrackedSeconds);
    }
  }, [bestTrackedSeconds, session.trackedSeconds, session.updateTrackedSeconds]);
  useEffect(() => {
    const prev = prevStatusRef.current;
    const next = session.status;
    if (prev !== next) {
      callbacksRef.current.onStatusChange?.(prev, next);
      if (next === "failed") {
        callbacksRef.current.onCompilationFailed?.();
      }
      prevStatusRef.current = next;
    }
  }, [session.status]);
  const takeScreenshotRef = useRef(capture.takeScreenshot);
  takeScreenshotRef.current = capture.takeScreenshot;
  const captureUploadConfirmRef = useRef(uploader.captureUploadConfirm);
  captureUploadConfirmRef.current = uploader.captureUploadConfirm;
  const isActive = session.status === "active" || session.status === "pending";
  useSilentAudioKeepAlive(capture.isSharing && isActive);
  useEffect(() => {
    if (!capture.isSharing || !isActive) return;
    capturingRef.current = true;
    let cancelled = false;
    const frameIntervalMs = session.frameIntervalMs ?? CLIP_FRAME_INTERVAL_MS;
    let clipRecorder = null;
    if (captureMode !== "camera" && session.clipsEnabled && ClipRecorder.isSupported()) {
      const video = screenCapture.getVideo();
      if (video) {
        try {
          clipRecorder = new ClipRecorder(video, frameIntervalMs, {
            maxWidth: config.capture.maxWidth,
            maxHeight: config.capture.maxHeight,
            jpegQuality: config.capture.jpegQuality,
            // Denser cadence for the short opening clip, which is cut after
            // CLIP_FIRST_CUT_DELAY_MS — well under one frame interval — so
            // that first upload carries a few frames rather than one.
            openingFrameIntervalMs: Math.max(
              500,
              Math.round(CLIP_FIRST_CUT_DELAY_MS / 4)
            )
          });
          clipRecorder.start();
        } catch (err) {
          console.warn(
            "[lookout] clip recorder unavailable, using JPEG captures:",
            err
          );
          clipRecorder = null;
        }
      }
    }
    clipRecorderRef.current = clipRecorder;
    let inFlight = null;
    let clipFailures = 0;
    const disableClips = (why) => {
      if (!clipRecorderRef.current) return;
      console.warn(
        `[lookout] ${why} \u2014 recording one JPEG per minute for the rest of this session.`
      );
      clipRecorderRef.current.stop();
      clipRecorderRef.current = null;
    };
    const uploadWithFallback = async (payload) => {
      const isClip = payload.format != null && payload.format !== "jpeg";
      try {
        const result = await captureUploadConfirmRef.current(payload);
        if (isClip) clipFailures = 0;
        return result;
      } catch (err) {
        if (!isClip) throw err;
        if (err instanceof ClipFormatRejectedError) {
          disableClips("the server no longer accepts clips for this session");
        } else if (++clipFailures >= MAX_CLIP_UPLOAD_FAILURES) {
          disableClips(
            `${clipFailures} consecutive clip uploads failed`
          );
        }
        if (!payload.previewBlob) throw err;
        console.warn("[lookout] clip upload failed \u2014 retrying as a JPEG:", err);
        return await captureUploadConfirmRef.current({
          blob: payload.previewBlob,
          width: payload.width,
          height: payload.height,
          capturedAtMs: payload.capturedAtMs
        });
      }
    };
    const settleInFlight = async () => {
      if (!inFlight) return null;
      const pending = inFlight;
      try {
        return (await pending).nextExpectedAt;
      } catch (err) {
        console.warn("[lookout] capture upload failed:", err);
        return null;
      } finally {
        if (inFlight === pending) inFlight = null;
      }
    };
    const scheduleNext = (nextExpectedAt) => {
      if (cancelled) return;
      const target = nextExpectedAt ? Date.parse(nextExpectedAt) : Date.now() + config.capture.intervalMs;
      const delay = Math.min(
        config.capture.intervalMs * 2,
        Math.max(0, target - Date.now())
      );
      if (intervalRef.current !== null) clearTimeout(intervalRef.current);
      intervalRef.current = setTimeout(tick, delay);
    };
    const tick = async () => {
      if (cancelled) return;
      let nextExpectedAt = await settleInFlight();
      if (cancelled) return;
      try {
        let payload = await clipRecorderRef.current?.cut() ?? null;
        if (payload?.truncated) {
          console.warn(
            "[lookout] clip hit its frame cap \u2014 uploads are running behind the capture cadence, so this clip covers a longer window."
          );
        }
        if (!payload) {
          payload = await takeScreenshotRef.current();
        }
        if (payload) {
          callbacksRef.current.onCapture?.(payload);
          inFlight = uploadWithFallback(payload);
          const pending = inFlight;
          pending.then((result) => {
            if (cancelled || inFlight !== pending) return;
            inFlight = null;
            scheduleNext(result.nextExpectedAt);
          }).catch(() => {
          });
        }
      } catch (err) {
        console.warn("[lookout] capture cycle failed:", err);
      }
      if (cancelled) return;
      scheduleNext(nextExpectedAt);
    };
    if (clipRecorder) {
      intervalRef.current = setTimeout(tick, CLIP_FIRST_CUT_DELAY_MS);
    } else {
      tick();
    }
    return () => {
      capturingRef.current = false;
      cancelled = true;
      if (intervalRef.current !== null) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
      clipRecorderRef.current?.stop();
      clipRecorderRef.current = null;
    };
  }, [
    capture.isSharing,
    isActive,
    config.capture.intervalMs,
    config.capture.maxWidth,
    config.capture.maxHeight,
    config.capture.jpegQuality,
    captureMode,
    session.clipsEnabled,
    session.frameIntervalMs,
    screenCapture.getVideo
  ]);
  const wasSharingRef = useRef(capture.isSharing);
  useEffect(() => {
    const wasSharing = wasSharingRef.current;
    wasSharingRef.current = capture.isSharing;
    if (!wasSharing && capture.isSharing && session.status === "paused") {
      intentionalPauseRef.current = false;
      session.resume().then(() => {
        callbacksRef.current.onResume?.();
      }).catch(() => {
      });
    }
  }, [capture.isSharing, session.status, session.resume]);
  useEffect(() => {
    if (!capture.isSharing && session.status === "active") {
      if (capturingRef.current) {
        capturingRef.current = false;
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        clipRecorderRef.current?.stop();
        clipRecorderRef.current = null;
        callbacksRef.current.onShareStop?.();
      }
      session.pause().catch(() => {
      });
    }
  }, [capture.isSharing, session.status, session.pause]);
  useEffect(() => {
    if (uploader.sessionConflict) {
      session.syncStatus().then(() => uploader.resetConflict());
    }
  }, [uploader.sessionConflict, session.syncStatus, uploader.resetConflict]);
  useEffect(() => {
    if (capture.isSharing && session.status === "paused" && !intentionalPauseRef.current) {
      session.resume().then(() => {
        callbacksRef.current.onResume?.();
      }).catch(() => {
      });
    }
  }, [capture.isSharing, session.status, session.resume]);
  useEffect(() => {
    if (config.autoStart && !capture.isSharing && (session.status === "pending" || session.status === "active")) {
      capture.startSharing().catch(() => {
      });
    }
  }, [config.autoStart, session.status, capture.isSharing, capture.startSharing]);
  const startSharing = useCallback(async () => {
    try {
      await capture.startSharing();
      callbacksRef.current.onShareStart?.();
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      let message;
      const isCamera = captureMode === "camera";
      if (e.name === "NotAllowedError") {
        message = isCamera ? "Camera permission was denied. Please allow camera access and try again." : "Screen sharing permission was denied. Please try again and select a screen to share.";
      } else if (e.name === "AbortError") {
        message = isCamera ? "Camera access was cancelled." : "Screen sharing was cancelled.";
      } else {
        message = e.message || (isCamera ? "Failed to start camera." : "Failed to start screen sharing.");
      }
      callbacksRef.current.onError?.(new Error(message), "startSharing");
      session.setError(message);
    }
  }, [capture.startSharing, session, captureMode]);
  const stopSharing = useCallback(() => {
    capture.stopSharing();
    callbacksRef.current.onShareStop?.();
  }, [capture.stopSharing]);
  const pause = useCallback(async () => {
    if (pauseInFlightRef.current) return;
    pauseInFlightRef.current = true;
    intentionalPauseRef.current = true;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    clipRecorderRef.current?.stop();
    clipRecorderRef.current = null;
    capturingRef.current = false;
    try {
      await session.pause();
      callbacksRef.current.onPause?.({ totalActiveSeconds: session.totalActiveSeconds });
    } finally {
      pauseInFlightRef.current = false;
    }
  }, [session.pause, session.totalActiveSeconds]);
  const resume = useCallback(async () => {
    if (resumeInFlightRef.current) return;
    resumeInFlightRef.current = true;
    intentionalPauseRef.current = false;
    try {
      await session.resume();
      callbacksRef.current.onResume?.();
    } finally {
      resumeInFlightRef.current = false;
    }
  }, [session.resume]);
  const stop = useCallback(async (options) => {
    if (stopInFlightRef.current) return;
    stopInFlightRef.current = true;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    clipRecorderRef.current?.stop();
    clipRecorderRef.current = null;
    capturingRef.current = false;
    capture.stopSharing();
    try {
      await session.stop(options?.name, { edit: options?.edit });
      callbacksRef.current.onStop?.({
        trackedSeconds: session.trackedSeconds,
        totalActiveSeconds: session.totalActiveSeconds
      });
    } finally {
      stopInFlightRef.current = false;
    }
  }, [session.stop, session.trackedSeconds, session.totalActiveSeconds, capture.stopSharing]);
  const [videoUrl, setVideoUrl] = useState(null);
  useEffect(() => {
    if (session.status !== "complete") return;
    let cancelled = false;
    client.getVideo().then((data) => {
      if (!cancelled) {
        setVideoUrl(data.videoUrl);
        callbacksRef.current.onComplete?.({ videoUrl: data.videoUrl });
      }
    }).catch(() => {
    });
    return () => {
      cancelled = true;
    };
  }, [session.status, client]);
  const isRecording = capture.isSharing && (session.status === "active" || session.status === "pending");
  const state = {
    status: session.status,
    isSharing: capture.isSharing,
    isRecording,
    trackedSeconds: bestTrackedSeconds,
    displaySeconds,
    screenshotCount: Math.max(session.screenshotCount, uploader.uploads.completed),
    uploads: uploader.uploads,
    lastScreenshotUrl: uploader.lastScreenshotUrl,
    videoUrl,
    error: session.error,
    captureMode,
    availableCameras: cameraCapture.devices,
    selectedCameraId: cameraCapture.selectedDeviceId,
    isPreviewing: cameraCapture.isPreviewing,
    previewStream: cameraCapture.previewStream
  };
  const actions = {
    startSharing,
    stopSharing,
    pause,
    resume,
    stop,
    selectCamera: cameraCapture.selectDevice,
    startPreview: cameraCapture.startPreview,
    stopPreview: cameraCapture.stopPreview
  };
  return { state, actions };
}
function StatusBar({ displaySeconds, screenshotCount, uploads }) {
  return /* @__PURE__ */ jsxs("div", { style: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `${spacing.md}px ${spacing.xl}px`,
    background: colors.bg.surface,
    borderRadius: radii.md,
    marginBottom: spacing.lg
  }, children: [
    /* @__PURE__ */ jsx("div", { style: {
      fontSize: fontSize.timer,
      fontWeight: fontWeight.bold,
      fontVariantNumeric: "tabular-nums",
      color: colors.text.primary
    }, children: formatTime(displaySeconds) }),
    /* @__PURE__ */ jsxs("div", { style: { display: "flex", gap: spacing.lg, fontSize: fontSize.lg, color: colors.text.secondary }, children: [
      /* @__PURE__ */ jsxs("span", { children: [
        screenshotCount,
        " ",
        screenshotCount === 1 ? "capture" : "captures"
      ] }),
      uploads.pending > 0 && /* @__PURE__ */ jsxs("span", { style: { color: colors.status.warning }, children: [
        uploads.pending,
        " uploading..."
      ] }),
      uploads.failed > 0 && /* @__PURE__ */ jsxs("span", { style: { color: colors.status.danger }, children: [
        uploads.failed,
        " failed"
      ] })
    ] })
  ] });
}
function unitAtTime(t, unitCount) {
  return Math.max(0, Math.min(unitCount - 1, Math.floor(t)));
}
function regionsToCuts(regions, units) {
  return regions.filter((r) => r.endUnit > r.startUnit).map((r) => {
    const lastCut = Date.parse(units[r.endUnit - 1].capturedAt);
    const nextKept = r.endUnit < units.length ? Date.parse(units[r.endUnit].capturedAt) : null;
    const end = nextKept === null ? lastCut + SCREENSHOT_INTERVAL_MS : Math.min(nextKept, lastCut + SCREENSHOT_INTERVAL_MS);
    return {
      start: units[r.startUnit].capturedAt,
      end: new Date(end).toISOString()
    };
  });
}
function cutsToRegions(cuts, units) {
  const regions = [];
  let open = null;
  for (let i = 0; i < units.length; i++) {
    const cut = isCutAt(Date.parse(units[i].capturedAt), cuts);
    if (cut) {
      if (open) open.endUnit = i + 1;
      else open = { startUnit: i, endUnit: i + 1 };
    } else if (open) {
      regions.push(open);
      open = null;
    }
  }
  if (open) regions.push(open);
  return regions;
}
function normalizeRegions(regions) {
  const sorted = regions.filter((r) => r.endUnit > r.startUnit).slice().sort((a, b) => a.startUnit - b.startUnit);
  const merged = [];
  for (const r of sorted) {
    const last = merged[merged.length - 1];
    if (last && r.startUnit <= last.endUnit) {
      last.endUnit = Math.max(last.endUnit, r.endUnit);
    } else {
      merged.push({ ...r });
    }
  }
  return merged;
}
function regionAtTime(t, regions) {
  return regions.find((r) => t >= r.startUnit && t < r.endUnit) ?? null;
}
function gapIndices(units) {
  const gaps = [];
  for (let i = 1; i < units.length; i++) {
    const delta = Date.parse(units[i].capturedAt) - Date.parse(units[i - 1].capturedAt);
    if (delta > SCREENSHOT_INTERVAL_MS * 1.5) gaps.push(i);
  }
  return gaps;
}
function unitClockLabel(unit) {
  const d = new Date(unit.capturedAt);
  return d.toLocaleTimeString(void 0, {
    hour: "numeric",
    minute: "2-digit"
  });
}
function elapsedLabel(unitIndex, totalUnits) {
  const m = Math.max(0, Math.round(unitIndex));
  if (totalUnits < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}:${String(m % 60).padStart(2, "0")}`;
}
var NICE_STEPS = [1, 2, 5, 10, 15, 20, 30, 60, 120, 180, 360, 720];
function rulerStep(unitCount, trackWidthPx, minLabelPx = 88) {
  if (unitCount <= 0 || trackWidthPx <= 0) return 1;
  const pxPerUnit = trackWidthPx / unitCount;
  const needed = minLabelPx / pxPerUnit;
  return NICE_STEPS.find((s) => s >= needed) ?? NICE_STEPS[NICE_STEPS.length - 1];
}
function rulerTicks(unitCount, step) {
  const ticks = [];
  if (unitCount <= 0 || step <= 0) return ticks;
  const half = step / 2;
  for (let u = 0; u <= unitCount; u += half) {
    const major = Math.abs(u / step - Math.round(u / step)) < 1e-9;
    ticks.push({ unit: u, major });
  }
  return ticks;
}
function useEditLease(client, active = true) {
  const [held, setHeld] = useState(true);
  const heldRef = useRef(true);
  heldRef.current = held;
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const beat = async () => {
      if (cancelled || !heldRef.current) return;
      try {
        const res = await client.heartbeatEditing();
        if (!cancelled && !res.held) setHeld(false);
      } catch {
      }
    };
    void beat();
    const id = setInterval(beat, EDIT_HEARTBEAT_SECONDS * 1e3);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [client, active]);
  return held;
}

// src/hooks/buildProgress.ts
var COMPILE_BASE_MS = 6e3;
var COMPILE_MS_PER_UNIT = 90;
function compileEstimateMs(units) {
  return COMPILE_BASE_MS + Math.max(0, units) * COMPILE_MS_PER_UNIT;
}
function estimateBuildProgress(elapsedMs, estimateMs) {
  if (estimateMs <= 0) return 0;
  return 1 - Math.exp(-2.2 * (Math.max(0, elapsedMs) / estimateMs));
}
var PROGRESS_POLL_MS = 2e3;
var PROGRESS_CAP = 0.95;
function interpolateBuildProgress(anchor, sinceAnchorMs, estimateMs) {
  const from = Math.max(0, Math.min(PROGRESS_CAP, anchor));
  if (estimateMs <= 0) return from;
  const budget = Math.min(PROGRESS_CAP - from, PROGRESS_POLL_MS / estimateMs);
  if (budget <= 0) return from;
  const eased = 1 - Math.exp(-2.2 * (Math.max(0, sinceAnchorMs) / PROGRESS_POLL_MS));
  return Math.min(PROGRESS_CAP, from + budget * eased);
}

// src/components/editorStyles.ts
var EASE_OUT_QUART = "cubic-bezier(0.25, 1, 0.5, 1)";
var EDITOR_STYLE_ID = "lookout-editor-styles";
function injectEditorStyles() {
  if (typeof document === "undefined") return;
  if (document.querySelector(`style[data-${EDITOR_STYLE_ID}]`)) return;
  const style = document.createElement("style");
  style.setAttribute(`data-${EDITOR_STYLE_ID}`, "");
  style.textContent = `
    .lk-ed-strip { transition: box-shadow 180ms ${EASE_OUT_QUART}; }
    .lk-ed-strip:focus-visible {
      outline: none;
      box-shadow: 0 0 0 2px var(--color-bg-body), 0 0 0 4px var(--color-accent);
    }

    .lk-ed-region {
      transition: background-color 140ms ${EASE_OUT_QUART},
                  box-shadow 140ms ${EASE_OUT_QUART};
    }
    .lk-ed-region:hover { background-color: var(--color-cut-fill-hover); }

    /* The grab target is deliberately wider than the visible grip: 12px of
       hit area, a 3px bar. Fitts's law on a 1-second-per-minute timeline. */
    .lk-ed-grip { transition: transform 140ms ${EASE_OUT_QUART}; }
    .lk-ed-handle:hover .lk-ed-grip { transform: scaleX(1.6); }

    /* The cap is a grab target, so it acknowledges the pointer \u2014 but
       subtly: it marks a position, it shouldn't dominate the timeline.
       The hover is driven from the (larger, invisible) hit area. */
    .lk-ed-playhead { transition: transform 120ms ${EASE_OUT_QUART}; }
    *:hover > .lk-ed-playhead { transform: scaleX(1.25); }
    *:active > .lk-ed-playhead { transform: scaleX(1.1); }

    .lk-ed-iconbtn {
      display: inline-flex; align-items: center; justify-content: center;
      background: transparent; cursor: pointer; padding: 0;
      color: var(--color-text-primary);
      border: 1px solid var(--color-border-default);
      transition: background-color 140ms ${EASE_OUT_QUART},
                  border-color 140ms ${EASE_OUT_QUART},
                  transform 140ms ${EASE_OUT_QUART};
    }
    .lk-ed-iconbtn:hover {
      background: var(--color-bg-surface);
      border-color: var(--color-border-hover);
    }
    .lk-ed-iconbtn:active { transform: scale(0.94); }
    .lk-ed-iconbtn:focus-visible {
      outline: none;
      box-shadow: 0 0 0 2px var(--color-bg-body), 0 0 0 4px var(--color-accent);
    }

    .lk-ed-fade-in { animation: lk-ed-fade 160ms ${EASE_OUT_QUART} both; }
    @keyframes lk-ed-fade {
      from { opacity: 0; transform: translateY(4px); }
      to   { opacity: 1; transform: none; }
    }

    /* Motion here is all feedback \u2014 grip growth, region tint, the scrubber
       card arriving. Under reduced-motion the states still change, they
       just stop moving. */
    @media (prefers-reduced-motion: reduce) {
      .lk-ed-strip, .lk-ed-region, .lk-ed-grip, .lk-ed-iconbtn,
      .lk-ed-playhead {
        transition-duration: 1ms;
      }
      .lk-ed-fade-in { animation-duration: 1ms; }
      .lk-ed-handle:hover .lk-ed-grip { transform: none; }
      *:hover > .lk-ed-playhead { transform: none; }
    }
  `;
  document.head.appendChild(style);
}
var sizes = { sm: 16, md: 24, lg: 40 };
function Spinner({ size = "md", color }) {
  const s = sizes[size];
  const baseColor = colors.spinner.base;
  const trackColor = color || colors.spinner.track;
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      width: s,
      height: s,
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      style: {
        animation: "spin 1s linear infinite",
        flexShrink: 0
      },
      children: [
        /* @__PURE__ */ jsx(
          "circle",
          {
            cx: "12",
            cy: "12",
            r: "10",
            stroke: baseColor,
            strokeWidth: "2"
          }
        ),
        /* @__PURE__ */ jsx(
          "circle",
          {
            cx: "12",
            cy: "12",
            r: "10",
            stroke: trackColor,
            strokeWidth: "2",
            strokeLinecap: "round",
            strokeDasharray: "62.83",
            strokeDashoffset: "47.12"
          }
        )
      ]
    }
  );
}
var variantStyles = {
  primary: { background: colors.accent.base, color: colors.accent.on, border: "1px solid transparent" },
  success: { background: colors.status.success, color: "#fff", border: "1px solid transparent" },
  danger: { background: colors.status.danger, color: "#fff", border: "1px solid transparent" },
  warning: { background: colors.status.warning, color: "#000", border: "1px solid transparent" },
  secondary: { background: "transparent", color: colors.text.secondary, border: `1px solid ${colors.border.hover}` },
  ghost: { background: "transparent", color: colors.text.secondary, border: "1px solid transparent" }
};
var sizeStyles = {
  sm: { padding: "6px 12px", fontSize: 12 },
  md: { padding: "8px 16px", fontSize: 13 },
  lg: { padding: "12px 24px", fontSize: 15 }
};
function Button({
  variant = "primary",
  size = "md",
  loading = false,
  fullWidth = false,
  disabled,
  children,
  style,
  ...rest
}) {
  const isDisabled = disabled || loading;
  const { background, border, borderRadius, color, ...outerStyle } = style || {};
  const idleBackground = background ?? variantStyles[variant].background;
  const idleBorder = border ?? variantStyles[variant].border;
  const hoverBackground = background ?? (variant === "ghost" ? colors.bg.selected : variant === "secondary" ? colors.bg.surface : (
    // The accent darkens on hover. Derived with color-mix so a
    // brand colour supplied by an embedder gets a matching hover
    // state without them having to provide a second shade.
    variant === "primary" ? colors.accent.hover : variantStyles[variant].background
  ));
  const hoverBorder = border ?? (variant === "ghost" ? "1px solid transparent" : variantStyles[variant].border);
  return /* @__PURE__ */ jsxs(
    motion.button,
    {
      whileHover: isDisabled ? void 0 : "hover",
      whileTap: isDisabled ? void 0 : "active",
      initial: "idle",
      disabled: isDisabled,
      style: {
        position: "relative",
        fontWeight: fontWeight.semibold,
        borderRadius: borderRadius ?? radii.md,
        cursor: isDisabled ? "not-allowed" : "pointer",
        opacity: isDisabled ? 0.6 : 1,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: fullWidth ? "100%" : void 0,
        padding: 0,
        color: color ?? variantStyles[variant].color,
        background: "transparent",
        border: "1px solid transparent",
        ...outerStyle
      },
      ...rest,
      children: [
        /* @__PURE__ */ jsx(Squircle, { cornerRadius: borderRadius ?? radii.md, cornerSmoothing: 0.7, asChild: true, children: /* @__PURE__ */ jsx(
          motion.div,
          {
            variants: {
              idle: { scale: 1, background: idleBackground, border: idleBorder },
              hover: { scale: 1, background: hoverBackground, border: hoverBorder },
              active: { scale: 0.96, background: hoverBackground, border: hoverBorder }
            },
            transition: { type: "spring", stiffness: 1500, damping: 60 },
            style: {
              position: "absolute",
              inset: -1,
              background: idleBackground,
              border: idleBorder,
              transition: "opacity 0.15s, background 0.15s, border-color 0.15s"
            }
          }
        ) }),
        /* @__PURE__ */ jsxs(
          "span",
          {
            style: {
              position: "relative",
              zIndex: 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              width: "100%",
              ...sizeStyles[size]
            },
            children: [
              loading && /* @__PURE__ */ jsx(Spinner, { size: "sm", color: variant === "warning" ? "#000" : "#fff" }),
              children
            ]
          }
        )
      ]
    }
  );
}
function MinutesFlow({ minutes, color }) {
  const safe = Math.max(0, Math.round(minutes));
  const h = Math.floor(safe / 60);
  const m = safe % 60;
  const style = { color, fontVariantNumeric: "tabular-nums" };
  if (h === 0) {
    return /* @__PURE__ */ jsxs("span", { style, children: [
      /* @__PURE__ */ jsx(NumberFlow, { value: m }),
      "m"
    ] });
  }
  return /* @__PURE__ */ jsxs("span", { style, children: [
    /* @__PURE__ */ jsx(NumberFlow, { value: h }),
    "h",
    " ",
    /* @__PURE__ */ jsx(NumberFlow, { value: m }),
    "m"
  ] });
}
function ProgressRing({
  progress,
  size = 72,
  strokeWidth = 5,
  showPercent = false,
  color
}) {
  const clamped = Math.max(0, Math.min(1, progress));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - clamped);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: { position: "relative", width: size, height: size, flexShrink: 0 },
      role: "progressbar",
      "aria-valuemin": 0,
      "aria-valuemax": 100,
      "aria-valuenow": Math.round(clamped * 100),
      children: [
        /* @__PURE__ */ jsxs("svg", { width: size, height: size, style: { display: "block", transform: "rotate(-90deg)" }, children: [
          /* @__PURE__ */ jsx(
            "circle",
            {
              cx: size / 2,
              cy: size / 2,
              r: radius,
              fill: "none",
              stroke: colors.spinner.base,
              strokeWidth
            }
          ),
          /* @__PURE__ */ jsx(
            "circle",
            {
              cx: size / 2,
              cy: size / 2,
              r: radius,
              fill: "none",
              stroke: color ?? colors.accent.base,
              strokeWidth,
              strokeLinecap: "round",
              strokeDasharray: circumference,
              strokeDashoffset: offset,
              style: { transition: "stroke-dashoffset 0.25s linear" }
            }
          )
        ] }),
        showPercent && /* @__PURE__ */ jsxs(
          "div",
          {
            style: {
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: fontSize.md,
              fontWeight: fontWeight.semibold,
              color: colors.text.secondary,
              fontVariantNumeric: "tabular-nums"
            },
            children: [
              /* @__PURE__ */ jsx(NumberFlow, { value: Math.min(99, Math.floor(clamped * 100)) }),
              "%"
            ]
          }
        )
      ]
    }
  );
}
function ErrorDisplay({ error, variant = "banner", title, reassurance, onDismiss, onCopy, action }) {
  if (variant === "page") {
    return /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: spacing.xxl, textAlign: "center" }, children: [
      /* @__PURE__ */ jsx("h2", { style: { fontSize: fontSize.heading, fontWeight: fontWeight.bold, color: colors.status.danger, marginBottom: spacing.sm }, children: title || "Error" }),
      /* @__PURE__ */ jsx("pre", { style: { margin: 0, fontSize: fontSize.xs, fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-all", maxWidth: 400, maxHeight: 150, overflowY: "auto", color: colors.text.error, marginBottom: spacing.lg }, children: error }),
      /* @__PURE__ */ jsxs("div", { style: { display: "flex", gap: spacing.sm, justifyContent: "center" }, children: [
        action && /* @__PURE__ */ jsx(Button, { variant: "primary", size: "md", onClick: action.onClick, children: action.label }),
        onCopy && /* @__PURE__ */ jsx(Button, { variant: "secondary", size: "md", onClick: onCopy, children: "Copy Error" })
      ] })
    ] });
  }
  if (variant === "inline") {
    return /* @__PURE__ */ jsx("pre", { style: { margin: 0, fontSize: fontSize.xs, fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-all", color: colors.text.error, maxHeight: 150, overflowY: "auto" }, children: error });
  }
  return /* @__PURE__ */ jsxs("div", { style: { padding: `${spacing.md}px ${spacing.lg}px`, marginBottom: spacing.md, background: "rgba(239,68,68,0.15)", border: `1px solid ${colors.status.danger}`, borderRadius: radii.md, color: colors.text.error, fontSize: fontSize.md, display: "flex", alignItems: "flex-start", gap: spacing.sm }, children: [
    /* @__PURE__ */ jsxs("div", { style: { flex: 1, margin: 0, fontSize: fontSize.xs, whiteSpace: "pre-wrap", wordBreak: "break-all", maxHeight: 120, overflowY: "auto" }, children: [
      title && /* @__PURE__ */ jsx("strong", { style: { display: "block", marginBottom: spacing.xs }, children: title }),
      /* @__PURE__ */ jsx("span", { style: { fontFamily: "monospace" }, children: error }),
      reassurance && /* @__PURE__ */ jsx("span", { style: { opacity: 0.8 }, children: `

${reassurance}` })
    ] }),
    action && /* @__PURE__ */ jsxs(
      motion.button,
      {
        onClick: action.onClick,
        whileTap: "active",
        initial: "idle",
        style: { background: "transparent", border: "none", color: colors.text.error, cursor: "pointer", fontSize: fontSize.xs, lineHeight: 1, padding: "2px 8px", borderRadius: radii.sm, whiteSpace: "nowrap", position: "relative" },
        children: [
          /* @__PURE__ */ jsx(
            motion.div,
            {
              variants: { idle: { scale: 1 }, active: { scale: 0.96 } },
              transition: { type: "spring", stiffness: 1500, damping: 60 },
              style: { position: "absolute", inset: 0, borderRadius: radii.sm, border: "1px solid " + colors.text.error, zIndex: 0 }
            }
          ),
          /* @__PURE__ */ jsx("span", { style: { position: "relative", zIndex: 1, display: "inline-block" }, children: action.label })
        ]
      }
    ),
    onCopy && /* @__PURE__ */ jsxs(
      motion.button,
      {
        onClick: onCopy,
        whileTap: "active",
        initial: "idle",
        style: { background: "transparent", border: "none", color: colors.text.error, cursor: "pointer", fontSize: fontSize.xs, lineHeight: 1, padding: "2px 8px", borderRadius: radii.sm, whiteSpace: "nowrap", position: "relative" },
        children: [
          /* @__PURE__ */ jsx(
            motion.div,
            {
              variants: { idle: { scale: 1 }, active: { scale: 0.96 } },
              transition: { type: "spring", stiffness: 1500, damping: 60 },
              style: { position: "absolute", inset: 0, borderRadius: radii.sm, border: "1px solid " + colors.text.error, zIndex: 0 }
            }
          ),
          /* @__PURE__ */ jsx("span", { style: { position: "relative", zIndex: 1, display: "inline-block" }, children: "Copy" })
        ]
      }
    ),
    onDismiss && /* @__PURE__ */ jsxs(
      motion.button,
      {
        onClick: onDismiss,
        whileTap: "active",
        initial: "idle",
        style: { background: "none", border: "none", color: colors.text.error, cursor: "pointer", fontSize: fontSize.xl, lineHeight: 1, padding: 0, position: "relative" },
        children: [
          /* @__PURE__ */ jsx(
            motion.div,
            {
              variants: { idle: { scale: 1 }, active: { scale: 0.96 } },
              transition: { type: "spring", stiffness: 1500, damping: 60 },
              style: { position: "absolute", inset: -2, borderRadius: "50%", background: "transparent", zIndex: 0 }
            }
          ),
          /* @__PURE__ */ jsx("span", { style: { position: "relative", zIndex: 1, display: "inline-block" }, children: "\xD7" })
        ]
      }
    )
  ] });
}
var STRIP_HEIGHT = 56;
var hatch = (periodPx) => `repeating-linear-gradient(45deg, ${colors.editor.cutStripe} 0 ${periodPx / 2}px, transparent ${periodPx / 2}px ${periodPx}px)`;
var RULER_HEIGHT = 22;
var HEAD_W = 9;
var HEAD_H = 13;
var HEAD_HIT = 22;
var FILMSTRIP_MAX_TILES = 48;
var pixelRatio = () => Math.min(2, typeof window === "undefined" ? 1 : window.devicePixelRatio || 1);
function TimelapseEditor({
  token,
  apiBaseUrl,
  onApplied,
  onCancel,
  onCutsChange
}) {
  const client = useMemo(
    () => createLookoutClient({ baseUrl: apiBaseUrl, token }),
    [apiBaseUrl, token]
  );
  useEffect(() => injectEditorStyles(), []);
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [preparingUnits, setPreparingUnits] = useState(null);
  const [buildProgress, setBuildProgress] = useState(0);
  const [realProgress, setRealProgress] = useState(null);
  const realAnchorRef = useRef(null);
  const prepareStartRef = useRef(null);
  const [regions, setRegions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [filmstrip, setFilmstrip] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const videoRef = useRef(null);
  const timelineRef = useRef(null);
  const dragRef = useRef(null);
  const regionsRef = useRef(regions);
  regionsRef.current = regions;
  const rafRef = useRef(0);
  const units = data?.units ?? [];
  const unitCount = units.length;
  useEffect(() => {
    let cancelled = false;
    let timer;
    const fail = (reason) => setLoadError(
      reason === "published" ? "This timelapse has already been published, so it can't be edited." : reason === "failed" ? "This timelapse couldn't be compiled, so there's nothing to edit." : reason === "recompiles_exhausted" ? "This timelapse has reached its edit limit." : "This timelapse isn't available for editing."
    );
    const loadUnits = async () => {
      const res = await client.getUnits();
      if (cancelled) return;
      if (res.editable && res.originalVideoUrl) {
        setPreparingUnits(null);
        setData(res);
        setRegions(cutsToRegions(res.cuts, res.units));
        return;
      }
      if (res.editableReason === "preparing" || res.editableReason === "no_original") {
        setPreparingUnits(res.expectedUnits ?? 0);
        timer = setTimeout(waitForReady, 2e3);
        return;
      }
      fail(res.editableReason);
    };
    const waitForReady = async () => {
      if (cancelled) return;
      try {
        const status = await client.getStatus();
        if (cancelled) return;
        if (typeof status.progress === "number") setRealProgress(status.progress);
        if (status.editable) {
          await loadUnits();
          return;
        }
        if (status.status === "complete") {
          fail("published");
          return;
        }
        if (status.status === "failed") {
          fail("failed");
          return;
        }
      } catch (err) {
        console.warn("[editor] status poll failed:", err);
      }
      timer = setTimeout(waitForReady, 2e3);
    };
    void (async () => {
      try {
        await loadUnits();
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [client]);
  useEffect(() => {
    if (realProgress === null) return;
    realAnchorRef.current = { value: realProgress, atMs: Date.now() };
    setBuildProgress((prev) => Math.max(prev, realProgress));
  }, [realProgress]);
  useEffect(() => {
    if (preparingUnits === null) {
      prepareStartRef.current = null;
      return;
    }
    if (prepareStartRef.current === null) prepareStartRef.current = Date.now();
    const startedAt = prepareStartRef.current;
    const estimateMs = compileEstimateMs(preparingUnits);
    const tick = () => {
      const now = Date.now();
      const anchor = realAnchorRef.current;
      const estimated = estimateBuildProgress(now - startedAt, estimateMs);
      const fromReal = anchor ? interpolateBuildProgress(anchor.value, now - anchor.atMs, estimateMs) : 0;
      setBuildProgress(
        (prev) => Math.min(PROGRESS_CAP, Math.max(prev, estimated, fromReal))
      );
    };
    tick();
    const id = setInterval(tick, 200);
    return () => clearInterval(id);
  }, [preparingUnits]);
  const leaseHeld = useEditLease(client, !saving);
  useEffect(() => {
    if (leaseHeld || saving) return;
    setLoadError(
      "This timelapse was already published, so it can no longer be edited."
    );
  }, [leaseHeld, saving]);
  useEffect(() => {
    const tick = () => {
      const v = videoRef.current;
      if (v) setTime(v.currentTime);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTimeUpdate = () => {
      if (v.paused) return;
      const region = regionAtTime(v.currentTime, regionsRef.current);
      if (!region) return;
      if (region.endUnit >= unitCount) {
        v.pause();
        v.currentTime = region.startUnit;
      } else {
        v.currentTime = region.endUnit;
      }
    };
    v.addEventListener("timeupdate", onTimeUpdate);
    return () => v.removeEventListener("timeupdate", onTimeUpdate);
  }, [unitCount, data?.originalVideoUrl]);
  const [frameSrc, setFrameSrc] = useState(null);
  useEffect(() => {
    const src = data?.originalVideoUrl;
    if (!src) return;
    let cancelled = false;
    let objectUrl = null;
    const probe = (url, useCors) => new Promise((resolve) => {
      const probeEl = document.createElement("video");
      probeEl.crossOrigin = "anonymous";
      probeEl.muted = true;
      probeEl.preload = "metadata";
      probeEl.onloadedmetadata = () => {
        probeEl.removeAttribute("src");
        resolve(true);
      };
      probeEl.onerror = () => resolve(false);
      probeEl.src = url;
    });
    (async () => {
      if (await probe(src)) {
        if (!cancelled) setFrameSrc(src);
        return;
      }
      console.warn(
        "[editor] preview video is not CORS-readable; fetching bytes for thumbnails"
      );
      try {
        const res = await fetch(src);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setFrameSrc(objectUrl);
      } catch (err) {
        console.error("[editor] no frame source available for thumbnails:", err);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setFrameSrc(null);
    };
  }, [data?.originalVideoUrl]);
  const [stripWidth, setStripWidth] = useState(0);
  useEffect(() => {
    const el = timelineRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(
      ([entry]) => setStripWidth(entry.contentRect.width)
    );
    ro.observe(el);
    setStripWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, [data]);
  const [tileAspect, setTileAspect] = useState(16 / 9);
  const [tileWidth, setTileWidth] = useState(Math.round(STRIP_HEIGHT * (16 / 9)));
  useEffect(() => {
    if (!frameSrc || unitCount === 0 || stripWidth <= 0) return;
    let cancelled = false;
    let v = null;
    const timer = setTimeout(() => {
      v = document.createElement("video");
      if (!frameSrc.startsWith("blob:")) v.crossOrigin = "anonymous";
      v.muted = true;
      v.preload = "auto";
      v.src = frameSrc;
      const el = v;
      void (async () => {
        try {
          await new Promise((resolve, reject) => {
            el.onloadedmetadata = () => resolve();
            el.onerror = () => reject(new Error("filmstrip video load failed"));
          });
          if (cancelled) return;
          const aspect = el.videoWidth / Math.max(1, el.videoHeight);
          setTileAspect(aspect);
          const tileW = Math.max(24, Math.round(STRIP_HEIGHT * aspect));
          setTileWidth(tileW);
          const tiles = Math.min(
            FILMSTRIP_MAX_TILES,
            Math.max(1, Math.ceil(stripWidth / tileW))
          );
          const dpr = pixelRatio();
          const canvas = document.createElement("canvas");
          canvas.width = Math.round(tileW * dpr);
          canvas.height = Math.round(STRIP_HEIGHT * dpr);
          const ctx = canvas.getContext("2d");
          if (!ctx) return;
          ctx.imageSmoothingQuality = "high";
          const thumbs = [];
          for (let i = 0; i < tiles; i++) {
            if (cancelled) return;
            const frac = Math.min(1, (i + 0.5) * tileW / stripWidth);
            const t = Math.min(el.duration - 0.05, frac * el.duration);
            await new Promise((resolve) => {
              el.onseeked = () => resolve();
              el.currentTime = Math.max(0, t);
            });
            ctx.drawImage(el, 0, 0, canvas.width, canvas.height);
            thumbs.push(canvas.toDataURL("image/jpeg", 0.82));
            if (i % 3 === 2) setFilmstrip([...thumbs]);
          }
          if (!cancelled) setFilmstrip(thumbs);
        } catch (err) {
          console.error("[editor] filmstrip generation failed:", err);
        } finally {
          el.removeAttribute("src");
          el.load();
        }
      })();
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (v) {
        v.removeAttribute("src");
        v.load();
      }
    };
  }, [frameSrc, unitCount, stripWidth]);
  const unitFromEvent = useCallback(
    (e) => {
      const el = timelineRef.current;
      if (!el || unitCount === 0) return 0;
      const rect = el.getBoundingClientRect();
      const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      return frac * unitCount;
    },
    [unitCount]
  );
  const seekTo = useCallback(
    (t) => {
      const v = videoRef.current;
      if (!v) return;
      v.currentTime = Math.max(0, Math.min(unitCount - 0.05, t));
    },
    [unitCount]
  );
  const beginDrag = useCallback((e, state) => {
    dragRef.current = state;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    e.preventDefault();
    e.stopPropagation();
  }, []);
  const onTimelinePointerDown = useCallback(
    (e) => {
      if (saving) return;
      beginDrag(e, { kind: "maybe", downUnitF: unitFromEvent(e) });
    },
    [beginDrag, saving, unitFromEvent]
  );
  const onRulerPointerDown = useCallback(
    (e) => {
      if (saving) return;
      beginDrag(e, { kind: "scrub" });
      seekTo(unitFromEvent(e));
    },
    [beginDrag, saving, seekTo, unitFromEvent]
  );
  const onPointerMove = useCallback(
    (e) => {
      const drag = dragRef.current;
      if (!drag) return;
      const unitF = unitFromEvent(e);
      if (drag.kind === "scrub") {
        seekTo(unitF);
        return;
      }
      if (drag.kind === "maybe") {
        if (Math.abs(unitF - drag.downUnitF) < 0.34) return;
        const a = Math.floor(Math.min(unitF, drag.downUnitF));
        const b = Math.ceil(Math.max(unitF, drag.downUnitF));
        setRegions((prev) => {
          const next = [...prev, { startUnit: a, endUnit: Math.max(b, a + 1) }];
          setSelected(next.length - 1);
          return next;
        });
        dragRef.current = {
          kind: "region",
          index: regionsRef.current.length,
          mode: unitF >= drag.downUnitF ? "end" : "start",
          grabOffset: 0,
          anchorUnit: Math.floor(drag.downUnitF)
        };
        return;
      }
      setRegions((prev) => {
        const next = prev.map((r2) => ({ ...r2 }));
        const r = next[drag.index];
        if (!r) return prev;
        if (drag.mode === "move") {
          const width = r.endUnit - r.startUnit;
          let start = Math.round(unitF - drag.grabOffset);
          start = Math.max(0, Math.min(unitCount - width, start));
          r.startUnit = start;
          r.endUnit = start + width;
        } else if (drag.mode === "start") {
          r.startUnit = Math.max(0, Math.min(r.endUnit - 1, Math.round(unitF)));
          seekTo(r.startUnit + 0.02);
        } else {
          const anchor = drag.anchorUnit;
          const rounded = Math.round(unitF);
          if (rounded <= anchor) {
            r.startUnit = Math.max(0, rounded);
            r.endUnit = anchor + 1;
            seekTo(r.startUnit + 0.02);
          } else {
            r.endUnit = Math.min(unitCount, Math.max(r.startUnit + 1, rounded));
            seekTo(Math.min(unitCount - 0.05, r.endUnit + 0.02));
          }
        }
        return next;
      });
      setSelected(drag.index);
    },
    [seekTo, unitCount, unitFromEvent]
  );
  const onPointerUp = useCallback(() => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag) return;
    if (drag.kind === "maybe") {
      seekTo(drag.downUnitF);
      setSelected(null);
      return;
    }
    if (drag.kind === "region") {
      const dragged = regionsRef.current[drag.index];
      const next = normalizeRegions(regionsRef.current);
      setRegions(next);
      const idx = dragged ? next.findIndex(
        (r) => dragged.startUnit >= r.startUnit && dragged.startUnit < r.endUnit
      ) : -1;
      setSelected(idx >= 0 ? idx : null);
    }
  }, [seekTo]);
  const onRegionPointerDown = useCallback(
    (e, index, mode) => {
      if (saving) return;
      const r = regionsRef.current[index];
      if (!r) return;
      setSelected(index);
      beginDrag(e, {
        kind: "region",
        index,
        mode,
        grabOffset: unitFromEvent(e) - r.startUnit,
        anchorUnit: r.startUnit
      });
    },
    [beginDrag, saving, unitFromEvent]
  );
  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      const region = regionAtTime(v.currentTime, regionsRef.current);
      if (region && region.endUnit < unitCount) v.currentTime = region.endUnit;
      void v.play();
    } else {
      v.pause();
    }
  }, [unitCount]);
  const cutHere = useCallback(() => {
    const v = videoRef.current;
    if (!v || unitCount === 0) return;
    const at = unitAtTime(v.currentTime, unitCount);
    setRegions((prev) => {
      const next = normalizeRegions([...prev, { startUnit: at, endUnit: at + 1 }]);
      setSelected(next.findIndex((r) => at >= r.startUnit && at < r.endUnit));
      return next;
    });
  }, [unitCount]);
  useEffect(() => {
    const onKeyDown = (e) => {
      const target = e.target;
      if (target && ["INPUT", "TEXTAREA"].includes(target.tagName)) return;
      if (e.metaKey || e.ctrlKey) return;
      if (e.key === " " || e.key === "k") {
        e.preventDefault();
        togglePlay();
      } else if (e.key === "x" || e.key === "c") {
        e.preventDefault();
        cutHere();
      } else if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        if (selected !== null) {
          setRegions((prev) => prev.filter((_, i) => i !== selected));
          setSelected(null);
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        setSelected(null);
      } else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        const step2 = e.shiftKey ? 10 : 1;
        seekTo(
          (videoRef.current?.currentTime ?? 0) + (e.key === "ArrowLeft" ? -step2 : step2)
        );
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [selected, seekTo, togglePlay, cutHere]);
  const save = useCallback(async () => {
    if (!data) return;
    setSaving(true);
    setSaveError(null);
    try {
      const cuts = regionsToCuts(normalizeRegions(regionsRef.current), data.units);
      await client.setCuts(cuts);
      const result = await client.applyCuts();
      onApplied?.(result);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  }, [client, data, onApplied]);
  const normalized = useMemo(() => normalizeRegions(regions), [regions]);
  const serializedCuts = useMemo(
    () => data ? regionsToCuts(normalized, data.units) : [],
    [normalized, data]
  );
  const unitTimesMs = useMemo(
    () => units.map((u) => Date.parse(u.capturedAt)),
    [units]
  );
  const removedUnits = useMemo(
    () => countCutUnits(unitTimesMs, serializedCuts),
    [unitTimesMs, serializedCuts]
  );
  const keptUnits = unitCount - removedUnits;
  const allCut = unitCount > 0 && keptUnits === 0;
  const gaps = useMemo(() => data ? gapIndices(data.units) : [], [data]);
  const step = useMemo(
    () => rulerStep(unitCount, stripWidth),
    [unitCount, stripWidth]
  );
  const ticks = useMemo(() => rulerTicks(unitCount, step), [unitCount, step]);
  const currentUnit = unitAtTime(time, Math.max(1, unitCount));
  const inCutNow = regionAtTime(time, normalized) !== null;
  const pct = (u) => `${u / Math.max(1, unitCount) * 100}%`;
  const onCutsChangeRef = useRef(onCutsChange);
  onCutsChangeRef.current = onCutsChange;
  useEffect(() => {
    if (!data) return;
    const saved = JSON.stringify(data.cuts ?? []);
    onCutsChangeRef.current?.(
      serializedCuts,
      JSON.stringify(serializedCuts) !== saved
    );
  }, [serializedCuts, data]);
  if (loadError) {
    return /* @__PURE__ */ jsxs("div", { style: { padding: spacing.xl, maxWidth: 520 }, children: [
      /* @__PURE__ */ jsx(ErrorDisplay, { error: loadError, variant: "banner", title: "Can't edit" }),
      onCancel && /* @__PURE__ */ jsx("div", { style: { marginTop: spacing.md }, children: /* @__PURE__ */ jsx(Button, { variant: "secondary", size: "sm", onClick: onCancel, children: "Close" }) })
    ] });
  }
  if (!data) {
    return /* @__PURE__ */ jsxs(
      "div",
      {
        style: {
          height: "100%",
          minHeight: 260,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: spacing.lg,
          textAlign: "center",
          padding: spacing.xl
        },
        children: [
          preparingUnits !== null ? /* @__PURE__ */ jsx(ProgressRing, { progress: buildProgress, showPercent: true }) : /* @__PURE__ */ jsx(Spinner, { size: "lg" }),
          /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx(
              "div",
              {
                style: {
                  fontSize: fontSize.xl,
                  fontWeight: fontWeight.semibold,
                  color: colors.text.primary,
                  letterSpacing: "-0.01em"
                },
                children: "Preparing your timelapse"
              }
            ),
            /* @__PURE__ */ jsx(
              "div",
              {
                style: {
                  fontSize: fontSize.md,
                  color: colors.text.secondary,
                  marginTop: spacing.xs,
                  maxWidth: 340,
                  lineHeight: 1.5
                },
                children: preparingUnits !== null && preparingUnits > 0 ? `Stitching ${preparingUnits} minute${preparingUnits === 1 ? "" : "s"} of footage.` : "oooooooooooo"
              }
            )
          ] })
        ]
      }
    );
  }
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        gap: spacing.md
      },
      children: [
        /* @__PURE__ */ jsxs(
          "div",
          {
            style: {
              flex: "1 1 auto",
              minHeight: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              background: colors.editor.well,
              border: `1px solid ${colors.editor.wellBorder}`,
              borderRadius: 12,
              overflow: "hidden"
            },
            children: [
              /* @__PURE__ */ jsx(
                "video",
                {
                  ref: videoRef,
                  src: data.originalVideoUrl ?? void 0,
                  playsInline: true,
                  muted: true,
                  onClick: togglePlay,
                  onPlay: () => setPlaying(true),
                  onPause: () => setPlaying(false),
                  style: {
                    maxWidth: "100%",
                    maxHeight: "100%",
                    display: "block",
                    cursor: "pointer"
                  }
                }
              ),
              /* @__PURE__ */ jsx(AnimatePresence, { children: !playing && /* @__PURE__ */ jsx(
                motion.div,
                {
                  initial: { opacity: 0, scale: 0.88 },
                  animate: { opacity: 1, scale: 1 },
                  exit: { opacity: 0, scale: 0.88 },
                  transition: { duration: 0.16, ease: [0.25, 1, 0.5, 1] },
                  style: {
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "none"
                  },
                  children: /* @__PURE__ */ jsx(
                    "div",
                    {
                      style: {
                        width: 56,
                        height: 56,
                        borderRadius: "50%",
                        background: "rgba(0,0,0,0.55)",
                        backdropFilter: "blur(12px)",
                        WebkitBackdropFilter: "blur(12px)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      },
                      children: /* @__PURE__ */ jsx("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "#fff", "aria-hidden": "true", children: /* @__PURE__ */ jsx("path", { d: "M8 5v14l11-7z" }) })
                    }
                  )
                }
              ) }),
              /* @__PURE__ */ jsx(AnimatePresence, { children: inCutNow && /* @__PURE__ */ jsx(
                motion.div,
                {
                  initial: { opacity: 0 },
                  animate: { opacity: 1 },
                  exit: { opacity: 0 },
                  transition: { duration: 0.12 },
                  style: {
                    position: "absolute",
                    inset: 0,
                    // Matches the stage's radius: an inset ring on a square
                    // box inside a rounded, clipped parent gets sliced at the
                    // corners and reads as a rendering glitch.
                    borderRadius: 12,
                    boxShadow: `inset 0 0 0 3px ${colors.editor.cutBorder}`,
                    // Tint only, no hatch: the stage is already showing the
                    // frame you're judging, and texture over live footage
                    // fights it. The hatch belongs on the timeline, where the
                    // question is "which stretch", not "what's in it".
                    backgroundColor: "rgba(220, 38, 38, 0.10)",
                    pointerEvents: "none"
                  },
                  children: /* @__PURE__ */ jsx(
                    "span",
                    {
                      style: {
                        position: "absolute",
                        top: spacing.md,
                        right: spacing.md,
                        background: colors.editor.cutBorder,
                        color: "#fff",
                        fontSize: fontSize.xs,
                        fontWeight: fontWeight.semibold,
                        padding: "3px 8px",
                        borderRadius: radii.sm,
                        letterSpacing: "0.01em"
                      },
                      children: "Will be removed"
                    }
                  )
                }
              ) })
            ]
          }
        ),
        /* @__PURE__ */ jsxs("div", { style: { flex: "0 0 auto", display: "flex", flexDirection: "column", gap: spacing.sm }, children: [
          /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: spacing.sm }, children: [
            /* @__PURE__ */ jsx(
              "button",
              {
                className: "lk-ed-iconbtn",
                onClick: togglePlay,
                "aria-label": playing ? "Pause" : "Play",
                style: { width: 30, height: 30, borderRadius: radii.md },
                children: playing ? /* @__PURE__ */ jsx("svg", { width: "12", height: "12", viewBox: "0 0 24 24", fill: "currentColor", "aria-hidden": "true", children: /* @__PURE__ */ jsx("path", { d: "M6 4h4v16H6zM14 4h4v16h-4z" }) }) : /* @__PURE__ */ jsx("svg", { width: "12", height: "12", viewBox: "0 0 24 24", fill: "currentColor", "aria-hidden": "true", children: /* @__PURE__ */ jsx("path", { d: "M8 5v14l11-7z" }) })
              }
            ),
            /* @__PURE__ */ jsxs(
              "div",
              {
                style: {
                  fontSize: fontSize.md,
                  color: colors.text.primary,
                  fontVariantNumeric: "tabular-nums",
                  letterSpacing: "-0.01em"
                },
                children: [
                  elapsedLabel(currentUnit, unitCount),
                  /* @__PURE__ */ jsxs("span", { style: { color: colors.text.tertiary }, children: [
                    " of ",
                    elapsedLabel(unitCount, unitCount),
                    " \xB7 recorded at ",
                    unitClockLabel(units[currentUnit])
                  ] })
                ]
              }
            ),
            /* @__PURE__ */ jsx("div", { style: { flex: 1 } })
          ] }),
          /* @__PURE__ */ jsxs(
            "div",
            {
              style: { position: "relative", userSelect: "none", touchAction: "none" },
              onPointerMove,
              onPointerUp,
              onPointerCancel: onPointerUp,
              children: [
                unitCount > 0 && /* @__PURE__ */ jsxs(
                  "div",
                  {
                    style: {
                      position: "absolute",
                      left: pct(Math.min(time, unitCount)),
                      top: 0,
                      bottom: 0,
                      width: 0,
                      zIndex: 3,
                      pointerEvents: "none"
                    },
                    children: [
                      /* @__PURE__ */ jsx(
                        "div",
                        {
                          onPointerDown: onRulerPointerDown,
                          "aria-hidden": "true",
                          style: {
                            position: "absolute",
                            top: RULER_HEIGHT - HEAD_H,
                            left: -HEAD_HIT / 2,
                            width: HEAD_HIT,
                            height: HEAD_HIT,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "flex-start",
                            cursor: "ew-resize",
                            pointerEvents: "auto"
                          },
                          children: /* @__PURE__ */ jsx(
                            "div",
                            {
                              className: "lk-ed-playhead",
                              style: {
                                width: HEAD_W,
                                height: HEAD_H,
                                borderRadius: HEAD_W / 2,
                                background: colors.text.primary,
                                boxShadow: "0 1px 3px rgba(0,0,0,0.45)"
                              }
                            }
                          )
                        }
                      ),
                      /* @__PURE__ */ jsx(
                        "div",
                        {
                          style: {
                            position: "absolute",
                            top: RULER_HEIGHT - 4,
                            bottom: 0,
                            left: -1,
                            width: 2,
                            background: colors.text.primary,
                            boxShadow: "0 0 0 0.5px rgba(0,0,0,0.35)"
                          }
                        }
                      )
                    ]
                  }
                ),
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    onPointerDown: onRulerPointerDown,
                    style: {
                      position: "relative",
                      height: RULER_HEIGHT,
                      cursor: "ew-resize"
                    },
                    children: ticks.map(({ unit, major }) => {
                      const left = unit / Math.max(1, unitCount) * 100;
                      return /* @__PURE__ */ jsxs("div", { style: { position: "absolute", left: `${left}%`, top: 0, bottom: 0 }, children: [
                        major && /* @__PURE__ */ jsx(
                          "span",
                          {
                            style: {
                              position: "absolute",
                              top: 0,
                              // First and last labels tuck inside the track
                              // instead of hanging off its edges.
                              left: unit === 0 ? 0 : void 0,
                              right: unit >= unitCount ? 0 : void 0,
                              transform: unit === 0 || unit >= unitCount ? void 0 : "translateX(-50%)",
                              fontSize: fontSize.xs,
                              color: colors.text.tertiary,
                              fontVariantNumeric: "tabular-nums",
                              whiteSpace: "nowrap",
                              pointerEvents: "none"
                            },
                            children: elapsedLabel(unit, unitCount)
                          }
                        ),
                        /* @__PURE__ */ jsx(
                          "div",
                          {
                            style: {
                              position: "absolute",
                              bottom: 0,
                              left: unit === 0 ? 0 : unit >= unitCount ? -1 : -0.5,
                              width: 1,
                              height: major ? 7 : 4,
                              background: major ? colors.text.tertiary : colors.text.quaternary,
                              pointerEvents: "none"
                            }
                          }
                        )
                      ] }, unit);
                    })
                  }
                ),
                /* @__PURE__ */ jsxs(
                  "div",
                  {
                    ref: timelineRef,
                    className: "lk-ed-strip",
                    tabIndex: 0,
                    role: "group",
                    "aria-label": "Timelapse timeline. Drag to remove a stretch of time.",
                    onPointerDown: onTimelinePointerDown,
                    style: {
                      position: "relative",
                      height: STRIP_HEIGHT,
                      borderRadius: radii.md,
                      overflow: "hidden",
                      cursor: "crosshair",
                      background: colors.editor.track,
                      border: `1px solid ${colors.border.default}`
                    },
                    children: [
                      /* @__PURE__ */ jsx(
                        "div",
                        {
                          style: {
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            pointerEvents: "none"
                          },
                          children: filmstrip.map((url, i) => /* @__PURE__ */ jsx(
                            "img",
                            {
                              src: url,
                              alt: "",
                              draggable: false,
                              style: {
                                width: tileWidth,
                                height: "100%",
                                flex: "0 0 auto",
                                objectFit: "fill",
                                display: "block"
                              }
                            },
                            i
                          ))
                        }
                      ),
                      gaps.map((i) => /* @__PURE__ */ jsx(
                        "div",
                        {
                          title: "Recording paused here",
                          style: {
                            position: "absolute",
                            left: pct(i),
                            top: 0,
                            bottom: 0,
                            width: 2,
                            marginLeft: -1,
                            background: "repeating-linear-gradient(180deg, var(--color-text-quaternary) 0 3px, transparent 3px 6px)",
                            pointerEvents: "none"
                          }
                        },
                        `gap-${i}`
                      )),
                      regions.map((r, i) => {
                        const isSelected = selected === i;
                        return /* @__PURE__ */ jsx(
                          "div",
                          {
                            className: "lk-ed-region",
                            onPointerDown: (e) => onRegionPointerDown(e, i, "move"),
                            style: {
                              position: "absolute",
                              left: pct(r.startUnit),
                              width: pct(r.endUnit - r.startUnit),
                              top: 0,
                              bottom: 0,
                              borderRadius: radii.sm,
                              // backgroundColor (not background) so the hover rule
                              // in editorStyles can swap the tint without dropping
                              // the hatch layered on top of it.
                              backgroundColor: colors.editor.cutFill,
                              backgroundImage: hatch(10),
                              boxShadow: isSelected ? `inset 0 0 0 2px ${colors.editor.cutBorder}` : `inset 0 0 0 1px ${colors.editor.cutBorder}`,
                              cursor: "grab",
                              boxSizing: "border-box"
                            },
                            children: [
                              { mode: "start", side: { left: -6 } },
                              { mode: "end", side: { right: -6 } }
                            ].map(({ mode, side }) => /* @__PURE__ */ jsx(
                              "div",
                              {
                                className: "lk-ed-handle",
                                onPointerDown: (e) => onRegionPointerDown(e, i, mode),
                                style: {
                                  position: "absolute",
                                  top: 0,
                                  bottom: 0,
                                  width: 12,
                                  ...side,
                                  cursor: "ew-resize",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center"
                                },
                                children: /* @__PURE__ */ jsx(
                                  "div",
                                  {
                                    className: "lk-ed-grip",
                                    style: {
                                      width: 3,
                                      height: 22,
                                      borderRadius: 2,
                                      background: colors.editor.cutBorder
                                    }
                                  }
                                )
                              },
                              mode
                            ))
                          },
                          i
                        );
                      })
                    ]
                  }
                )
              ]
            }
          ),
          /* @__PURE__ */ jsxs(
            "div",
            {
              style: {
                display: "flex",
                alignItems: "center",
                gap: spacing.md,
                flexWrap: "wrap",
                marginTop: spacing.xs
              },
              children: [
                /* @__PURE__ */ jsxs(
                  "div",
                  {
                    style: {
                      minWidth: 0,
                      fontSize: fontSize.lg,
                      color: colors.text.primary,
                      fontWeight: fontWeight.semibold,
                      letterSpacing: "-0.01em"
                    },
                    children: [
                      /* @__PURE__ */ jsx(MinutesFlow, { minutes: keptUnits }),
                      " kept",
                      removedUnits > 0 && /* @__PURE__ */ jsxs("span", { style: { color: colors.editor.cutBorder, fontWeight: fontWeight.medium }, children: [
                        " \xB7 ",
                        /* @__PURE__ */ jsx(MinutesFlow, { minutes: removedUnits, color: colors.editor.cutBorder }),
                        " removed"
                      ] })
                    ]
                  }
                ),
                /* @__PURE__ */ jsx("div", { style: { flex: 1, minWidth: spacing.md } }),
                selected !== null && /* @__PURE__ */ jsx(
                  Button,
                  {
                    variant: "secondary",
                    size: "sm",
                    onClick: () => {
                      setRegions((prev) => prev.filter((_, i) => i !== selected));
                      setSelected(null);
                    },
                    children: "Remove cut"
                  }
                ),
                normalized.length > 0 && /* @__PURE__ */ jsx(
                  Button,
                  {
                    variant: "secondary",
                    size: "sm",
                    onClick: () => {
                      setRegions([]);
                      setSelected(null);
                    },
                    children: "Clear all"
                  }
                ),
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    variant: "primary",
                    size: "sm",
                    onClick: save,
                    loading: saving,
                    disabled: allCut,
                    title: allCut ? "You can't remove the entire timelapse" : void 0,
                    children: saving ? "Saving\u2026" : "Save"
                  }
                )
              ]
            }
          ),
          saveError && /* @__PURE__ */ jsx(ErrorDisplay, { error: saveError, variant: "banner", title: "Couldn't save your edits" })
        ] })
      ]
    }
  );
}
function Card({ children, onClick, onContextMenu, padding, style }) {
  const content = /* @__PURE__ */ jsx(
    "div",
    {
      style: {
        background: colors.bg.surface,
        border: `1px solid ${colors.border.default}`,
        borderRadius: radii.lg,
        overflow: "hidden",
        padding,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        ...onClick ? {} : style
      },
      children
    }
  );
  if (!onClick) {
    return onContextMenu ? /* @__PURE__ */ jsx("div", { onContextMenu, style: { height: "100%" }, children: content }) : content;
  }
  return /* @__PURE__ */ jsx(
    motion.div,
    {
      onClick,
      onContextMenu,
      role: "button",
      tabIndex: 0,
      whileTap: { scale: 0.99 },
      transition: { type: "spring", stiffness: 1500, damping: 60 },
      style: {
        cursor: "pointer",
        display: "block",
        height: "100%",
        ...style
      },
      children: content
    }
  );
}
function Overlay({
  children,
  label,
  width = "min(1100px, 94vw)",
  height,
  onDismiss
}) {
  const panelRef = useRef(null);
  useEffect(() => {
    panelRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);
  useEffect(() => {
    if (!onDismiss) return;
    const onKey = (e) => {
      if (e.key === "Escape") onDismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDismiss]);
  if (typeof document === "undefined") return null;
  return createPortal(
    /* @__PURE__ */ jsx(
      "div",
      {
        "data-lookout-overlay": "",
        style: {
          position: "fixed",
          inset: 0,
          background: colors.bg.backdrop,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: spacing.xl,
          zIndex: 9999
        },
        onPointerDown: (e) => {
          if (onDismiss && e.target === e.currentTarget) onDismiss();
        },
        children: /* @__PURE__ */ jsx(
          motion.div,
          {
            ref: panelRef,
            role: "dialog",
            "aria-modal": "true",
            "aria-label": label,
            tabIndex: -1,
            initial: { opacity: 0, y: 10, scale: 0.99 },
            animate: { opacity: 1, y: 0, scale: 1 },
            transition: {
              y: { type: "spring", stiffness: 420, damping: 34, mass: 0.7 },
              opacity: { duration: 0.16, ease: "easeOut" }
            },
            style: {
              width,
              height,
              maxWidth: "100%",
              maxHeight: "100%",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              background: colors.bg.panel,
              border: `1px solid ${colors.border.default}`,
              borderRadius: 14,
              boxShadow: "0 24px 64px rgba(0,0,0,0.45)",
              outline: "none",
              overflow: "hidden"
            },
            children
          }
        )
      }
    ),
    document.body
  );
}
function StopChoiceModal({
  onResume,
  onStopAndSave,
  onEditAndSave,
  withName = false,
  loading = false
}) {
  const [name, setName] = useState("");
  const inputRef = useRef(null);
  const [choice, setChoice] = useState(null);
  useEffect(() => {
    if (withName) setTimeout(() => inputRef.current?.focus(), 50);
  }, [withName]);
  const value = () => name.trim() || null;
  return /* @__PURE__ */ jsx(Overlay, { label: "Finish this timelapse?", width: 420, children: /* @__PURE__ */ jsx("div", { style: { overflow: "auto" }, children: /* @__PURE__ */ jsxs(
    Card,
    {
      padding: spacing.xxl,
      style: {
        width: "100%",
        height: "auto",
        background: "transparent",
        border: "none"
      },
      children: [
        /* @__PURE__ */ jsx(
          "h3",
          {
            style: {
              fontSize: fontSize.xl,
              fontWeight: fontWeight.bold,
              color: colors.text.primary,
              margin: 0,
              marginBottom: spacing.sm
            },
            children: "Finish this timelapse?"
          }
        ),
        /* @__PURE__ */ jsx(
          "p",
          {
            style: {
              fontSize: fontSize.md,
              color: colors.text.secondary,
              margin: 0,
              marginBottom: spacing.lg
            },
            children: onEditAndSave ? "Save it as recorded, or review it first and cut out anything you'd rather not share." : "This ends the recording and compiles your timelapse."
          }
        ),
        withName && /* @__PURE__ */ jsx(
          "input",
          {
            ref: inputRef,
            type: "text",
            value: name,
            onChange: (e) => setName(e.target.value),
            onKeyDown: (e) => {
              if (e.key === "Enter" && !loading) {
                setChoice("stop");
                onStopAndSave(value());
              }
            },
            placeholder: "My timelapse",
            maxLength: 255,
            disabled: loading,
            style: {
              width: "100%",
              padding: `${spacing.md}px ${spacing.lg}px`,
              fontSize: fontSize.lg,
              fontWeight: fontWeight.medium,
              color: colors.text.primary,
              background: colors.bg.sunken,
              border: `1px solid ${colors.border.default}`,
              borderRadius: radii.md,
              outline: "none",
              boxSizing: "border-box",
              marginBottom: spacing.lg,
              opacity: loading ? 0.5 : 1
            }
          }
        ),
        /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", gap: spacing.sm }, children: [
          onEditAndSave && /* @__PURE__ */ jsx(
            Button,
            {
              variant: "primary",
              size: "lg",
              fullWidth: true,
              loading: loading && choice === "edit",
              disabled: loading && choice !== "edit",
              onClick: () => {
                setChoice("edit");
                onEditAndSave(value());
              },
              children: "Edit & save"
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              variant: onEditAndSave ? "secondary" : "primary",
              size: "lg",
              fullWidth: true,
              loading: loading && choice === "stop",
              disabled: loading && choice !== "stop",
              onClick: () => {
                setChoice("stop");
                onStopAndSave(value());
              },
              children: "Stop & save"
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              variant: "ghost",
              size: "md",
              fullWidth: true,
              disabled: loading,
              onClick: onResume,
              children: "Keep recording"
            }
          )
        ] })
      ]
    }
  ) }) });
}
function ScreenPreview({ imageUrl }) {
  if (!imageUrl) return null;
  return /* @__PURE__ */ jsxs("div", { style: styles.container, children: [
    /* @__PURE__ */ jsx("img", { src: imageUrl, alt: "Latest capture", style: styles.image }),
    /* @__PURE__ */ jsx("span", { style: styles.label, children: "Latest capture" })
  ] });
}
var styles = {
  container: {
    position: "relative",
    marginBottom: spacing.lg,
    borderRadius: radii.md,
    overflow: "hidden",
    background: colors.bg.sunken,
    border: `1px solid ${colors.border.default}`
  },
  image: { width: "100%", display: "block" },
  label: {
    position: "absolute",
    bottom: spacing.sm,
    right: spacing.sm,
    fontSize: fontSize.sm,
    color: colors.text.secondary,
    background: "rgba(0,0,0,0.5)",
    padding: "2px 8px",
    borderRadius: radii.sm
  }
};
function CameraPreview({ stream, fallbackImageUrl }) {
  const videoRef = useRef(null);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (stream) {
      video.srcObject = stream;
      video.play().catch(() => {
      });
    } else {
      video.srcObject = null;
    }
  }, [stream]);
  if (!stream && !fallbackImageUrl) return null;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        position: "relative",
        marginBottom: spacing.md,
        borderRadius: radii.md,
        overflow: "hidden",
        background: colors.bg.sunken,
        border: `1px solid ${colors.border.default}`
      },
      children: [
        stream ? /* @__PURE__ */ jsx(
          "video",
          {
            ref: videoRef,
            muted: true,
            playsInline: true,
            autoPlay: true,
            style: { width: "100%", display: "block", transform: "scaleX(-1)" }
          }
        ) : fallbackImageUrl && /* @__PURE__ */ jsx(
          "img",
          {
            src: fallbackImageUrl,
            alt: "Last captured frame",
            style: { width: "100%", display: "block" }
          }
        ),
        /* @__PURE__ */ jsx(
          "span",
          {
            style: {
              position: "absolute",
              bottom: 8,
              right: 8,
              fontSize: fontSize.xs,
              color: colors.text.tertiary,
              background: "rgba(0,0,0,0.7)",
              padding: "2px 8px",
              borderRadius: radii.sm
            },
            children: stream ? "Preview" : "Latest capture"
          }
        )
      ]
    }
  );
}
function CameraSelector({
  devices,
  selectedDeviceId,
  onSelect,
  disabled
}) {
  if (devices.length === 0) return null;
  return /* @__PURE__ */ jsxs("div", { style: { marginBottom: spacing.md }, children: [
    /* @__PURE__ */ jsx(
      "label",
      {
        style: {
          display: "block",
          fontSize: fontSize.sm,
          color: colors.text.secondary,
          marginBottom: spacing.xs
        },
        children: "Camera"
      }
    ),
    /* @__PURE__ */ jsx(
      "select",
      {
        value: selectedDeviceId ?? "",
        onChange: (e) => onSelect(e.target.value),
        disabled,
        style: {
          width: "100%",
          padding: `${spacing.sm}px ${spacing.md}px`,
          fontSize: fontSize.md,
          color: colors.text.primary,
          background: colors.bg.sunken,
          border: `1px solid ${colors.border.default}`,
          borderRadius: radii.md,
          outline: "none",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1
        },
        children: devices.map((device, i) => /* @__PURE__ */ jsx("option", { value: device.deviceId, children: device.label || `Camera ${i + 1}` }, device.deviceId))
      }
    )
  ] });
}
function RecordingControls({
  status,
  isSharing,
  onStartSharing,
  onPause,
  onResume,
  onStop,
  loading,
  captureMode = "screen"
}) {
  const isActive = status === "active" || status === "pending";
  const isPaused = status === "paused";
  return /* @__PURE__ */ jsxs("div", { style: {
    display: "flex",
    alignItems: "center",
    gap: spacing.md,
    justifyContent: "center",
    flexWrap: "wrap"
  }, children: [
    !isSharing && isActive && /* @__PURE__ */ jsx(Button, { variant: "success", size: "lg", onClick: onStartSharing, loading, children: captureMode === "camera" ? "Start Camera & Record" : "Share Screen & Start Recording" }),
    !isSharing && isPaused && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Button, { variant: "primary", size: "lg", onClick: onStartSharing, loading, children: captureMode === "camera" ? "Start Camera & Resume" : "Share Screen & Resume" }),
      /* @__PURE__ */ jsx(Button, { variant: "danger", size: "md", onClick: onStop, children: "Stop Session" })
    ] }),
    isSharing && isActive && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("div", { style: {
        width: 12,
        height: 12,
        borderRadius: "50%",
        background: colors.status.danger,
        animation: "pulse 1.5s ease-in-out infinite"
      } }),
      /* @__PURE__ */ jsx("span", { style: {
        fontSize: fontSize.lg,
        fontWeight: fontWeight.semibold,
        color: colors.status.danger,
        marginRight: spacing.sm
      }, children: "Recording" }),
      /* @__PURE__ */ jsx(Button, { variant: "warning", size: "md", onClick: onPause, children: "Pause" }),
      /* @__PURE__ */ jsx(Button, { variant: "danger", size: "md", onClick: onStop, children: "Stop" })
    ] })
  ] });
}

// #style-inject:#style-inject
function styleInject(css, { insertAt } = {}) {
  if (typeof document === "undefined") return;
  const head = document.head || document.getElementsByTagName("head")[0];
  const style = document.createElement("style");
  style.type = "text/css";
  if (insertAt === "top") {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }
  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

// src/components/VideoPlayer.css
styleInject(".lookout-video-player .media-button--mute,\n.lookout-video-player .media-slider--volume {\n  display: none !important;\n}\n.lookout-video-player.platform-windows .media-button--pip,\n.lookout-video-player.platform-mac .media-button--pip,\n.lookout-video-player.platform-linux .media-button--pip {\n  display: none !important;\n}\n.lookout-video-player.platform-linux .media-button--fullscreen {\n  display: none !important;\n}\n.lookout-video-player {\n  --media-border-radius: 8px;\n  --media-video-border-radius: 8px;\n}\n.lookout-video-player * {\n  --media-border-radius: 8px;\n}\n.lookout-video-player__error {\n  position: absolute;\n  inset: 0;\n  display: flex;\n  flex-direction: column;\n  align-items: center;\n  justify-content: center;\n  gap: 8px;\n  padding: 24px;\n  text-align: center;\n  background: rgba(11, 13, 18, 0.92);\n  color: #f4f5f7;\n  border-radius: 8px;\n  z-index: 2;\n}\n.lookout-video-player__error h3 {\n  margin: 0;\n  font-size: 18px;\n  font-weight: 600;\n}\n.lookout-video-player__error p {\n  margin: 0;\n  max-width: 480px;\n  font-size: 14px;\n  line-height: 1.5;\n  color: #c8ccd4;\n}\n.lookout-video-player__error-note {\n  font-size: 13px;\n  color: #9aa0aa;\n}\n.lookout-video-player__error a {\n  color: #6ea8ff;\n  text-decoration: underline;\n}\n");
var Player = createPlayer({ features: videoFeatures });
function VideoPlayer({ src }) {
  const [platform, setPlatform] = useState("");
  const [error, setError] = useState(null);
  const wrapperRef = useRef(null);
  useEffect(() => {
    if (typeof navigator !== "undefined") {
      const ua = navigator.userAgent.toLowerCase();
      if (ua.includes("linux") && !ua.includes("android")) {
        setPlatform("linux");
      } else if (ua.includes("win")) {
        setPlatform("windows");
      } else if (ua.includes("mac")) {
        setPlatform("mac");
      }
    }
  }, []);
  useEffect(() => {
    setError(null);
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const onVideoError = (event) => {
      const el = event.target;
      const code = el?.error?.code;
      if (code === 4 || code === 3) {
        setError({
          kind: "codec",
          detail: "Your system can't decode this video (H.264). On Linux, install gst-plugins-bad / OpenH264 to enable playback."
        });
      } else if (code === 2) {
        setError({
          kind: "network",
          detail: "Network error while loading the video. Check your connection and retry."
        });
      } else {
        setError({
          kind: "unknown",
          detail: el?.error?.message || "Playback failed for an unknown reason."
        });
      }
    };
    let videoEl = wrapper.querySelector("video");
    if (videoEl) videoEl.addEventListener("error", onVideoError, true);
    const observer = new MutationObserver(() => {
      const next = wrapper.querySelector("video");
      if (next && next !== videoEl) {
        if (videoEl) videoEl.removeEventListener("error", onVideoError, true);
        videoEl = next;
        videoEl.addEventListener("error", onVideoError, true);
      }
    });
    observer.observe(wrapper, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      if (videoEl) videoEl.removeEventListener("error", onVideoError, true);
    };
  }, [src]);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: wrapperRef,
      className: `lookout-video-player platform-${platform}`,
      style: {
        width: "100%",
        height: "100%",
        display: "flex",
        position: "relative",
        "--media-border-radius": "8px",
        "--media-video-border-radius": "8px"
      },
      children: [
        /* @__PURE__ */ jsx(Player.Provider, { children: /* @__PURE__ */ jsx(VideoSkin, { style: { width: "100%", height: "100%" }, children: /* @__PURE__ */ jsx(Video, { src, muted: true, playsInline: true, autoPlay: false }) }) }),
        error && /* @__PURE__ */ jsxs("div", { className: "lookout-video-player__error", role: "alert", children: [
          /* @__PURE__ */ jsx("h3", { children: "Can't play this timelapse" }),
          /* @__PURE__ */ jsx("p", { children: error.detail }),
          /* @__PURE__ */ jsxs("p", { className: "lookout-video-player__error-note", children: [
            "Your timelapse saved successfully. You can",
            " ",
            /* @__PURE__ */ jsx("a", { href: src, target: "_blank", rel: "noopener noreferrer", children: "download the file" }),
            " ",
            "and play it in another app."
          ] })
        ] })
      ]
    }
  );
}
function ProcessingState({ status, trackedSeconds, videoUrl, error, onVideoLoaded }) {
  const containerStyle = {
    width: "100%",
    borderRadius: 0,
    overflow: "hidden",
    background: colors.bg.sunken,
    aspectRatio: "16/9",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: spacing.md
  };
  if (status === "complete" && videoUrl) {
    return /* @__PURE__ */ jsx("div", { style: { width: "100%", borderRadius: 0, overflow: "hidden", background: colors.bg.sunken, aspectRatio: "16/9" }, children: /* @__PURE__ */ jsx(VideoPlayer, { src: videoUrl }) });
  }
  if (status === "complete" && error) {
    return /* @__PURE__ */ jsx("div", { style: containerStyle, children: /* @__PURE__ */ jsx("p", { style: { fontSize: fontSize.lg, color: colors.text.secondary, textAlign: "center" }, children: "No video available" }) });
  }
  if (status === "failed") {
    return /* @__PURE__ */ jsxs("div", { style: containerStyle, children: [
      /* @__PURE__ */ jsx("p", { style: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: colors.status.danger }, children: "Compilation failed" }),
      /* @__PURE__ */ jsx("p", { style: { fontSize: fontSize.md, color: colors.text.secondary }, children: "It will be retried automatically." })
    ] });
  }
  return /* @__PURE__ */ jsxs("div", { style: containerStyle, children: [
    /* @__PURE__ */ jsx(Spinner, { size: "lg" }),
    /* @__PURE__ */ jsx("p", { style: { fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: colors.text.primary }, children: status === "complete" ? "Loading video..." : "Compiling your timelapse..." }),
    /* @__PURE__ */ jsxs("p", { style: { fontSize: fontSize.md, color: colors.text.secondary }, children: [
      "Tracked time: ",
      formatTrackedTime(trackedSeconds)
    ] })
  ] });
}
function PageContainer({ children, maxWidth = 640, centered = false, style }) {
  return /* @__PURE__ */ jsx("div", { style: {
    maxWidth,
    margin: "0 auto",
    padding: spacing.lg,
    ...centered ? {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100%",
      flex: 1
    } : {},
    ...style
  }, children });
}
function LookoutRecorder({ editing = true } = {}) {
  const { state, actions } = useLookout();
  const { client, config } = useLookoutContext();
  const [resolvedToken, setResolvedToken] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  useEffect(() => {
    if (!editing) return;
    let cancelled = false;
    client.resolveToken().then((t) => {
      if (!cancelled) setResolvedToken(t);
    }).catch(() => {
    });
    return () => {
      cancelled = true;
    };
  }, [editing, client]);
  const canEdit = editing && resolvedToken !== null;
  const [stopPrompt, setStopPrompt] = useState(false);
  const [stopping, setStopping] = useState(false);
  const confirmStop = async (withEdit) => {
    setStopping(true);
    try {
      if (withEdit) setEditorOpen(true);
      await actions.stop({ edit: withEdit });
      setStopPrompt(false);
    } finally {
      setStopping(false);
    }
  };
  if (state.status === "loading") {
    return /* @__PURE__ */ jsx(PageContainer, { centered: true, children: /* @__PURE__ */ jsx(Spinner, { size: "lg" }) });
  }
  if (state.status === "no-token") {
    return /* @__PURE__ */ jsxs(PageContainer, { centered: true, children: [
      /* @__PURE__ */ jsx("h2", { style: { fontSize: fontSize.display, fontWeight: fontWeight.bold, color: colors.text.primary, marginBottom: spacing.sm }, children: "No session token" }),
      /* @__PURE__ */ jsx("p", { style: { fontSize: fontSize.xl, color: colors.text.secondary, textAlign: "center", maxWidth: 400 }, children: "This page requires a session token. You should have been redirected here from another service." })
    ] });
  }
  if (state.status === "error") {
    return /* @__PURE__ */ jsx(PageContainer, { centered: true, children: /* @__PURE__ */ jsx(ErrorDisplay, { error: state.error ?? "Unknown error", variant: "page" }) });
  }
  if (state.status === "stopped" || state.status === "compiling" || state.status === "complete" || state.status === "failed") {
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(PageContainer, { style: { padding: spacing.xxl }, children: /* @__PURE__ */ jsx(
        ProcessingState,
        {
          status: state.status,
          trackedSeconds: state.trackedSeconds,
          videoUrl: state.videoUrl
        }
      ) }),
      editorOpen && resolvedToken && state.status !== "failed" && /* @__PURE__ */ jsxs(Overlay, { label: "Review your timelapse", height: "min(820px, 92vh)", children: [
        /* @__PURE__ */ jsxs(
          "div",
          {
            style: {
              flex: "0 0 auto",
              padding: `${spacing.lg}px ${spacing.xl}px 0`
            },
            children: [
              /* @__PURE__ */ jsx(
                "div",
                {
                  style: {
                    fontSize: fontSize.xxl,
                    fontWeight: fontWeight.bold,
                    color: colors.text.primary,
                    letterSpacing: "-0.01em"
                  },
                  children: "Review your timelapse"
                }
              ),
              /* @__PURE__ */ jsx(
                "div",
                {
                  style: {
                    fontSize: fontSize.md,
                    color: colors.text.secondary,
                    marginTop: 2
                  },
                  children: "Cut anything you'd rather not share. Nothing is published until you save."
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              flex: "1 1 auto",
              minHeight: 0,
              padding: `${spacing.md}px ${spacing.xl}px ${spacing.xl}px`
            },
            children: /* @__PURE__ */ jsx(
              TimelapseEditor,
              {
                token: resolvedToken,
                apiBaseUrl: config.apiBaseUrl,
                onApplied: () => setEditorOpen(false)
              }
            )
          }
        )
      ] })
    ] });
  }
  const isCamera = state.captureMode === "camera";
  if (isCamera) {
    return /* @__PURE__ */ jsxs(PageContainer, { maxWidth: 800, style: { padding: spacing.xxl }, children: [
      /* @__PURE__ */ jsx(
        StatusBar,
        {
          displaySeconds: state.displaySeconds,
          screenshotCount: state.screenshotCount,
          uploads: state.uploads
        }
      ),
      state.availableCameras.length > 1 && /* @__PURE__ */ jsx(
        CameraSelector,
        {
          devices: state.availableCameras,
          selectedDeviceId: state.selectedCameraId,
          onSelect: actions.selectCamera,
          disabled: state.isSharing
        }
      ),
      state.isPreviewing || state.previewStream ? /* @__PURE__ */ jsx(
        CameraPreview,
        {
          stream: state.previewStream,
          fallbackImageUrl: state.lastScreenshotUrl
        }
      ) : state.lastScreenshotUrl ? /* @__PURE__ */ jsx(ScreenPreview, { imageUrl: state.lastScreenshotUrl }) : null,
      !state.isPreviewing && !state.isSharing ? (
        /* Phase 1: No stream yet — prompt to start camera */
        /* @__PURE__ */ jsx(
          CameraIdleControls,
          {
            status: state.status,
            onStartPreview: actions.startPreview,
            onStartRecording: actions.startSharing,
            onStop: () => setStopPrompt(true)
          }
        )
      ) : state.isPreviewing && !state.isSharing ? (
        /* Phase 2: Previewing — show "Start Recording" */
        /* @__PURE__ */ jsx(
          CameraPreviewControls,
          {
            onStartRecording: actions.startSharing,
            onStopPreview: actions.stopPreview
          }
        )
      ) : (
        /* Phase 3: Recording — standard recording controls */
        /* @__PURE__ */ jsx(
          RecordingControls,
          {
            status: state.status,
            isSharing: state.isSharing,
            onStartSharing: actions.startSharing,
            onPause: actions.pause,
            onResume: actions.resume,
            onStop: () => setStopPrompt(true),
            captureMode: "camera"
          }
        )
      ),
      stopPrompt && /* @__PURE__ */ jsx(
        StopChoiceModal,
        {
          loading: stopping,
          onResume: () => setStopPrompt(false),
          onStopAndSave: () => void confirmStop(false),
          onEditAndSave: canEdit ? () => void confirmStop(true) : void 0
        }
      )
    ] });
  }
  return /* @__PURE__ */ jsxs(PageContainer, { maxWidth: 800, style: { padding: spacing.xxl }, children: [
    /* @__PURE__ */ jsx(
      StatusBar,
      {
        displaySeconds: state.displaySeconds,
        screenshotCount: state.screenshotCount,
        uploads: state.uploads
      }
    ),
    /* @__PURE__ */ jsx(ScreenPreview, { imageUrl: state.lastScreenshotUrl }),
    /* @__PURE__ */ jsx(
      RecordingControls,
      {
        status: state.status,
        isSharing: state.isSharing,
        onStartSharing: actions.startSharing,
        onPause: actions.pause,
        onResume: actions.resume,
        onStop: () => setStopPrompt(true),
        captureMode: "screen"
      }
    ),
    stopPrompt && /* @__PURE__ */ jsx(
      StopChoiceModal,
      {
        loading: stopping,
        onResume: () => setStopPrompt(false),
        onStopAndSave: () => void confirmStop(false),
        onEditAndSave: canEdit ? () => void confirmStop(true) : void 0
      }
    )
  ] });
}
function CameraIdleControls({
  status,
  onStartPreview,
  onStartRecording,
  onStop
}) {
  const isPaused = status === "paused";
  return /* @__PURE__ */ jsx("div", { style: {
    display: "flex",
    alignItems: "center",
    gap: spacing.md,
    justifyContent: "center",
    flexWrap: "wrap"
  }, children: isPaused ? /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Button, { variant: "primary", size: "lg", onClick: onStartRecording, children: "Start Camera & Resume" }),
    /* @__PURE__ */ jsx(Button, { variant: "danger", size: "md", onClick: onStop, children: "Stop Session" })
  ] }) : /* @__PURE__ */ jsx(Button, { variant: "success", size: "lg", onClick: onStartPreview, children: "Start Camera" }) });
}
function CameraPreviewControls({
  onStartRecording,
  onStopPreview
}) {
  return /* @__PURE__ */ jsxs("div", { style: {
    display: "flex",
    alignItems: "center",
    gap: spacing.md,
    justifyContent: "center",
    flexWrap: "wrap"
  }, children: [
    /* @__PURE__ */ jsx(Button, { variant: "success", size: "lg", onClick: onStartRecording, children: "Start Recording" }),
    /* @__PURE__ */ jsx(Button, { variant: "secondary", size: "md", onClick: onStopPreview, children: "Cancel" })
  ] });
}
function ResultView({ status, trackedSeconds }) {
  const { client, config } = useLookoutContext();
  const [videoUrl, setVideoUrl] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (status === "complete") {
      client.getVideo().then((data) => {
        if (data.videoUrl && !data.videoUrl.startsWith("https://")) {
          throw new Error("Invalid video URL: must be HTTPS.");
        }
        setVideoUrl(data.videoUrl);
        config.callbacks.onComplete?.({ videoUrl: data.videoUrl });
      }).catch(
        (err) => setError(err instanceof Error ? err.message : "Failed to load video")
      );
    }
  }, [status, client, config.callbacks]);
  return /* @__PURE__ */ jsx(
    ProcessingState,
    {
      status,
      trackedSeconds,
      videoUrl,
      error
    }
  );
}
function Badge({ status, variant = "overlay", size = "sm" }) {
  const config = statusConfig[status] ?? { label: status, color: "var(--color-status-neutral)" };
  const isOverlay = variant === "overlay";
  const sizeStyles2 = {
    sm: { fontSize: fontSize.xs - 1, padding: "2px 8px" },
    md: { fontSize: fontSize.sm, padding: "4px 12px" },
    lg: { fontSize: fontSize.md, padding: "6px 16px" }
  };
  return /* @__PURE__ */ jsx("span", { style: {
    ...sizeStyles2[size],
    fontWeight: fontWeight.semibold,
    color: "#fff",
    // Keeping text white since background is usually a colorful status pill or dark neutral pill
    borderRadius: 999,
    background: config.color,
    ...isOverlay ? { boxShadow: `0 0 0 1px rgba(0,0,0,0.1)` } : {}
  }, children: config.label });
}
function SessionCard({ session, onClick, onArchive, onContextMenu }) {
  const date = new Date(session.createdAt);
  const dateStr = date.toLocaleDateString(void 0, {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== (/* @__PURE__ */ new Date()).getFullYear() ? "numeric" : void 0
  });
  return /* @__PURE__ */ jsxs(
    Card,
    {
      onClick,
      onContextMenu: onContextMenu ? (e) => {
        e.preventDefault();
        onContextMenu(e);
      } : void 0,
      style: { position: "relative" },
      children: [
        /* @__PURE__ */ jsxs("div", { style: { position: "relative", aspectRatio: "16/9", background: colors.bg.sunken, overflow: "hidden" }, children: [
          session.thumbnailUrl ? /* @__PURE__ */ jsx(
            "img",
            {
              src: session.thumbnailUrl,
              alt: "Timelapse thumbnail",
              style: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
              loading: "lazy"
            }
          ) : /* @__PURE__ */ jsx("div", { style: { width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: colors.bg.sunken }, children: /* @__PURE__ */ jsxs("svg", { width: "32", height: "32", viewBox: "0 0 24 24", fill: "none", stroke: colors.text.quaternary, strokeWidth: "1.5", children: [
            /* @__PURE__ */ jsx("rect", { x: "2", y: "3", width: "20", height: "14", rx: "2", ry: "2" }),
            /* @__PURE__ */ jsx("line", { x1: "8", y1: "21", x2: "16", y2: "21" }),
            /* @__PURE__ */ jsx("line", { x1: "12", y1: "17", x2: "12", y2: "21" })
          ] }) }),
          /* @__PURE__ */ jsx("span", { style: { position: "absolute", top: spacing.sm, right: spacing.sm }, children: /* @__PURE__ */ jsx(Badge, { status: session.status, variant: "overlay" }) })
        ] }),
        /* @__PURE__ */ jsxs("div", { style: { padding: `${spacing.md}px ${spacing.md}px` }, children: [
          /* @__PURE__ */ jsx("div", { style: {
            fontSize: fontSize.md,
            fontWeight: fontWeight.semibold,
            color: colors.text.primary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            marginBottom: 2
          }, children: session.name }),
          /* @__PURE__ */ jsxs("div", { style: { fontSize: fontSize.xs, color: colors.text.tertiary }, children: [
            formatTrackedTime(session.trackedSeconds),
            " \xB7 ",
            dateStr
          ] })
        ] }),
        onArchive && /* @__PURE__ */ jsxs(
          motion.button,
          {
            whileTap: "active",
            initial: "idle",
            style: {
              position: "absolute",
              top: spacing.sm,
              left: spacing.sm,
              width: 24,
              height: 24,
              borderRadius: "50%",
              background: "transparent",
              color: "var(--color-archive-icon, #fff)",
              border: "none",
              cursor: "pointer",
              fontSize: fontSize.lg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              zIndex: 10
            },
            onClick: (e) => {
              e.stopPropagation();
              onArchive();
            },
            onPointerDown: (e) => e.stopPropagation(),
            title: "Archive",
            children: [
              /* @__PURE__ */ jsx(
                motion.div,
                {
                  variants: { idle: { scale: 1 }, active: { scale: 0.99 } },
                  transition: { type: "spring", stiffness: 1500, damping: 60 },
                  style: {
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    background: "var(--color-archive-bg, rgba(0,0,0,0.6))",
                    backdropFilter: "blur(4px)",
                    WebkitBackdropFilter: "blur(4px)",
                    border: `1px solid var(--color-archive-border, rgba(255,255,255,0.1))`,
                    zIndex: 0,
                    transition: "all 0.15s"
                  },
                  onMouseEnter: (e) => {
                    e.currentTarget.style.background = "var(--color-archive-hover-bg, rgba(255,255,255,0.1))";
                    e.currentTarget.style.borderColor = "var(--color-archive-hover-border, rgba(255,255,255,0.2))";
                    if (e.currentTarget.parentElement) {
                      e.currentTarget.parentElement.style.color = "var(--color-text-error, #ef4444)";
                    }
                  },
                  onMouseLeave: (e) => {
                    e.currentTarget.style.background = "var(--color-archive-bg, rgba(0,0,0,0.6))";
                    e.currentTarget.style.borderColor = "var(--color-archive-border, rgba(255,255,255,0.1))";
                    if (e.currentTarget.parentElement) {
                      e.currentTarget.parentElement.style.color = "var(--color-archive-icon, #fff)";
                    }
                  }
                }
              ),
              /* @__PURE__ */ jsx("div", { style: { position: "relative", zIndex: 1, display: "flex", alignItems: "center", justifyContent: "center", transition: "color 0.15s" }, children: /* @__PURE__ */ jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2.5", strokeLinecap: "round", strokeLinejoin: "round", children: [
                /* @__PURE__ */ jsx("line", { x1: "18", y1: "6", x2: "6", y2: "18" }),
                /* @__PURE__ */ jsx("line", { x1: "6", y1: "6", x2: "18", y2: "18" })
              ] }) })
            ]
          }
        )
      ]
    }
  );
}
function Skeleton({ width, height, borderRadius = radii.md, aspectRatio, style }) {
  return /* @__PURE__ */ jsx("div", { style: {
    width: width ?? "100%",
    height: height ?? (aspectRatio ? void 0 : 20),
    aspectRatio,
    borderRadius,
    backgroundColor: colors.skeleton.bg,
    backgroundImage: `linear-gradient(90deg, transparent 0%, ${colors.skeleton.shimmer} 50%, transparent 100%)`,
    backgroundSize: "200% 100%",
    animation: "shimmer 1.5s ease-in-out infinite",
    ...style
  } });
}
function GallerySkeleton() {
  return /* @__PURE__ */ jsxs("div", { style: { padding: spacing.lg }, children: [
    /* @__PURE__ */ jsxs("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: spacing.lg }, children: [
      /* @__PURE__ */ jsx(Skeleton, { width: 180, height: 24 }),
      /* @__PURE__ */ jsx(Skeleton, { width: 36, height: 36, borderRadius: radii.sm })
    ] }),
    /* @__PURE__ */ jsx("div", { style: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: spacing.md }, children: [1, 2, 3].map((i) => /* @__PURE__ */ jsxs("div", { style: { borderRadius: radii.lg, overflow: "hidden" }, children: [
      /* @__PURE__ */ jsx(Skeleton, { aspectRatio: "16/9", borderRadius: 0 }),
      /* @__PURE__ */ jsxs("div", { style: { padding: `${spacing.md}px ${spacing.md}px`, background: colors.bg.surface }, children: [
        /* @__PURE__ */ jsx(Skeleton, { width: "60%", height: 16, style: { marginBottom: spacing.xs } }),
        /* @__PURE__ */ jsx(Skeleton, { width: "40%", height: 12 })
      ] })
    ] }, i)) })
  ] });
}
function SessionDetailSkeleton() {
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx(Skeleton, { aspectRatio: "16/9", borderRadius: radii.lg, style: { marginBottom: spacing.lg } }),
    /* @__PURE__ */ jsx(Skeleton, { width: "50%", height: 18, style: { marginBottom: spacing.xs } }),
    /* @__PURE__ */ jsx(Skeleton, { width: "30%", height: 12, style: { marginBottom: spacing.lg } }),
    /* @__PURE__ */ jsxs("div", { style: { display: "flex", gap: spacing.lg, justifyContent: "center" }, children: [
      /* @__PURE__ */ jsx(Skeleton, { height: 64, borderRadius: radii.md, style: { flex: 1 } }),
      /* @__PURE__ */ jsx(Skeleton, { height: 64, borderRadius: radii.md, style: { flex: 1 } })
    ] })
  ] });
}
function RecordPageSkeleton() {
  return /* @__PURE__ */ jsxs("div", { style: { maxWidth: 480, margin: "0 auto", padding: spacing.lg }, children: [
    /* @__PURE__ */ jsxs("div", { style: { display: "flex", justifyContent: "space-between", marginBottom: spacing.lg }, children: [
      /* @__PURE__ */ jsx(Skeleton, { width: 80, height: 32, borderRadius: radii.sm }),
      /* @__PURE__ */ jsx(Skeleton, { width: 100, height: 32, borderRadius: radii.sm })
    ] }),
    /* @__PURE__ */ jsx(Skeleton, { aspectRatio: "16/9", borderRadius: radii.lg, style: { marginBottom: spacing.lg } }),
    /* @__PURE__ */ jsx("div", { style: { display: "flex", flexDirection: "column", gap: spacing.sm }, children: [1, 2, 3].map((i) => /* @__PURE__ */ jsx(Skeleton, { height: 48, borderRadius: radii.md }, i)) }),
    /* @__PURE__ */ jsx(Skeleton, { height: 48, borderRadius: radii.lg, style: { marginTop: spacing.lg } })
  ] });
}
var addButtonStyle = {
  borderRadius: radii.md,
  fontSize: fontSize.xxl,
  width: 40,
  height: 40,
  padding: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center"
};
function GalleryHeader({ onAdd, onSettings }) {
  return /* @__PURE__ */ jsxs("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: spacing.lg, paddingBottom: 0, flexShrink: 0 }, children: [
    /* @__PURE__ */ jsx("h2", { style: { fontSize: fontSize.heading, fontWeight: fontWeight.bold, color: colors.text.primary, margin: 0 }, children: "Your Timelapses" }),
    /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: spacing.xs }, children: [
      onSettings && /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", onClick: onSettings, title: "Settings", "aria-label": "Settings", style: addButtonStyle, children: /* @__PURE__ */ jsx(GearSixIcon, { size: 30, weight: "fill", "aria-hidden": "true" }) }),
      onAdd && /* @__PURE__ */ jsx(
        Button,
        {
          variant: "ghost",
          size: "sm",
          onClick: (e) => {
            const r = e.currentTarget.getBoundingClientRect();
            onAdd({ x: r.left, y: r.top, width: r.width, height: r.height });
          },
          title: "Start",
          "aria-label": "Start",
          style: addButtonStyle,
          children: /* @__PURE__ */ jsx(PlusIcon, { size: 28, weight: "bold", "aria-hidden": "true" })
        }
      )
    ] })
  ] });
}
var galleryScrollPosition = 0;
function Gallery({
  sessions,
  loading,
  error,
  onSessionClick,
  onArchive,
  onSessionContextMenu,
  onRefresh,
  onAdd,
  onSettings,
  banner,
  showHeader = true,
  showBottomFade = true
}) {
  const scrollRef = useRef(null);
  const [showTopMask, setShowTopMask] = useState(false);
  const [showBottomMask, setShowBottomMask] = useState(false);
  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    galleryScrollPosition = scrollTop;
    setShowTopMask(scrollTop > 0);
    setShowBottomMask(Math.ceil(scrollTop + clientHeight) < scrollHeight);
  }, []);
  useEffect(() => {
    if (scrollRef.current && sessions.length > 0 && !loading) {
      scrollRef.current.scrollTop = galleryScrollPosition;
      handleScroll();
    }
  }, [sessions.length, loading, handleScroll]);
  useEffect(() => {
    handleScroll();
    window.addEventListener("resize", handleScroll);
    return () => window.removeEventListener("resize", handleScroll);
  }, [sessions, handleScroll]);
  const fadeMask = `linear-gradient(to bottom, ${showTopMask ? "transparent 0%, black 20px" : "black 0%, black 20px"}, ${showBottomMask && showBottomFade ? "black calc(100% - 20px), transparent 100%" : "black calc(100% - 20px), black 100%"})`;
  if (loading && sessions.length === 0) {
    return /* @__PURE__ */ jsx(GallerySkeleton, {});
  }
  if (error && sessions.length === 0) {
    return /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", height: "100%" }, children: [
      showHeader && /* @__PURE__ */ jsx(GalleryHeader, { onAdd, onSettings }),
      banner && /* @__PURE__ */ jsx("div", { style: { padding: spacing.lg, paddingBottom: 0 }, children: banner }),
      /* @__PURE__ */ jsxs("div", { style: { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: spacing.xxl }, children: [
        /* @__PURE__ */ jsx(ErrorDisplay, { error, variant: "inline" }),
        onRefresh && /* @__PURE__ */ jsx(Button, { variant: "primary", size: "md", onClick: onRefresh, style: { marginTop: spacing.md }, children: "Retry" })
      ] })
    ] });
  }
  if (sessions.length === 0) {
    return /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", height: "100%" }, children: [
      showHeader && /* @__PURE__ */ jsx(GalleryHeader, { onAdd, onSettings }),
      banner && /* @__PURE__ */ jsx("div", { style: { padding: spacing.lg, paddingBottom: 0 }, children: banner }),
      /* @__PURE__ */ jsxs("div", { style: { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: spacing.xxl }, children: [
        /* @__PURE__ */ jsx("p", { style: { marginBottom: spacing.md }, children: /* @__PURE__ */ jsxs("svg", { width: "48", height: "48", viewBox: "0 0 24 24", fill: "none", stroke: colors.text.primary, strokeWidth: "1.5", style: { opacity: 0.2 }, children: [
          /* @__PURE__ */ jsx("rect", { x: "2", y: "3", width: "20", height: "14", rx: "2", ry: "2" }),
          /* @__PURE__ */ jsx("line", { x1: "8", y1: "21", x2: "16", y2: "21" }),
          /* @__PURE__ */ jsx("line", { x1: "12", y1: "17", x2: "12", y2: "21" })
        ] }) }),
        /* @__PURE__ */ jsx("p", { style: { fontSize: fontSize.lg, color: colors.text.primary, opacity: 0.5, textAlign: "center" }, children: "No timelapses yet" }),
        /* @__PURE__ */ jsx("p", { style: { fontSize: fontSize.sm, color: colors.text.primary, opacity: 0.3, marginTop: spacing.xs, textAlign: "center" }, children: "Start a recording session to see it here." })
      ] })
    ] });
  }
  return /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", height: "100%" }, children: [
    showHeader && /* @__PURE__ */ jsx(GalleryHeader, { onAdd, onSettings }),
    banner && /* @__PURE__ */ jsx("div", { style: { padding: spacing.lg, paddingBottom: 0 }, children: banner }),
    /* @__PURE__ */ jsx(
      "div",
      {
        ref: scrollRef,
        onScroll: handleScroll,
        style: {
          flex: 1,
          overflowY: "auto",
          padding: spacing.lg,
          maskImage: fadeMask,
          WebkitMaskImage: fadeMask
        },
        children: /* @__PURE__ */ jsx("div", { style: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: spacing.md }, children: sessions.map((s) => /* @__PURE__ */ jsx(
          SessionCard,
          {
            session: s,
            onClick: () => onSessionClick?.(s.token),
            onArchive: onArchive ? () => onArchive(s.token) : void 0,
            onContextMenu: onSessionContextMenu ? (e) => onSessionContextMenu(s.token, e) : void 0
          },
          s.token
        )) })
      }
    )
  ] });
}
function HoldPanel({
  editable,
  progress,
  client,
  onEdit,
  onPublish
}) {
  const [publishing, setPublishing] = useState(false);
  useEditLease(client, !publishing);
  const [buildProgress, setBuildProgress] = useState(0);
  const startedAtRef = useRef(null);
  const sawRealRef = useRef(false);
  useEffect(() => {
    if (typeof progress !== "number") return;
    sawRealRef.current = true;
    setBuildProgress((prev) => Math.max(prev, progress));
  }, [progress]);
  useEffect(() => {
    if (editable) return;
    if (startedAtRef.current === null) startedAtRef.current = Date.now();
    const startedAt = startedAtRef.current;
    const tick = () => {
      if (sawRealRef.current) return;
      setBuildProgress(
        (prev) => Math.max(prev, estimateBuildProgress(Date.now() - startedAt, 3e4))
      );
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [editable]);
  return /* @__PURE__ */ jsxs(Card, { padding: spacing.lg, style: { marginBottom: spacing.lg }, children: [
    /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: spacing.lg }, children: [
      !editable && /* @__PURE__ */ jsx(ProgressRing, { size: 48, strokeWidth: 4, progress: buildProgress }),
      /* @__PURE__ */ jsxs("div", { style: { flex: 1, minWidth: 0 }, children: [
        /* @__PURE__ */ jsx("div", { style: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: colors.text.primary }, children: editable ? "Ready to review" : "Preparing your timelapse\u2026" }),
        /* @__PURE__ */ jsxs(
          "div",
          {
            style: {
              fontSize: fontSize.md,
              color: colors.text.secondary,
              marginTop: spacing.xs
            },
            children: [
              editable ? "Cut out anything you don't want to share, then save. Nothing is published until you do." : "Your recording is compiling. You'll be able to trim it in a moment.",
              " ",
              /* @__PURE__ */ jsx("span", { style: { color: colors.text.tertiary }, children: "If you close Lookout, it publishes as recorded." })
            ]
          }
        )
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { style: { display: "flex", gap: spacing.sm, marginTop: spacing.lg }, children: [
      /* @__PURE__ */ jsx(Button, { variant: "primary", size: "md", onClick: onEdit, disabled: !editable || publishing, children: "Edit & save" }),
      /* @__PURE__ */ jsx(
        Button,
        {
          variant: "secondary",
          size: "md",
          loading: publishing,
          onClick: async () => {
            setPublishing(true);
            try {
              await onPublish();
            } finally {
              setPublishing(false);
            }
          },
          children: "Publish as recorded"
        }
      )
    ] })
  ] });
}
function SessionDetail({
  token,
  apiBaseUrl,
  onBack,
  showBack = true,
  onArchive,
  onComplete,
  onEdit
}) {
  const [sessionInfo, setSessionInfo] = useState(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [isRenamingAnim, setIsRenamingAnim] = useState(false);
  const [editName, setEditName] = useState("");
  const inputRef = useRef(null);
  useEffect(() => {
    if (isRenaming) {
      setIsRenamingAnim(true);
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    } else {
      const t = setTimeout(() => setIsRenamingAnim(false), 600);
      return () => clearTimeout(t);
    }
  }, [isRenaming]);
  const [status, setStatus] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const client = useMemo(
    () => createLookoutClient({ baseUrl: apiBaseUrl, token }),
    [apiBaseUrl, token]
  );
  const prevStatusRef = useRef(null);
  const completeFiredRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/api/sessions/${token}`);
        if (res.ok) {
          const data = await res.json();
          setSessionInfo({ name: data.name, createdAt: data.createdAt });
        }
      } catch {
      }
    })();
  }, [token, apiBaseUrl]);
  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${apiBaseUrl}/api/sessions/${token}/status`);
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status} from /api/sessions/${token}/status
${body.slice(0, 500)}`);
      }
      const data = await res.json();
      setStatus(data);
      const prevStatus = prevStatusRef.current;
      prevStatusRef.current = data.status;
      if (data.status === "complete" && (prevStatus === "stopped" || prevStatus === "compiling") && !completeFiredRef.current) {
        completeFiredRef.current = true;
        onCompleteRef.current?.({ redirectUrl: data.redirectUrl ?? null });
      }
      if (data.status === "complete" && !videoUrl) {
        try {
          const vRes = await fetch(`${apiBaseUrl}/api/sessions/${token}/video`);
          if (vRes.ok) {
            const v = await vRes.json();
            setVideoUrl(v.videoUrl);
          }
        } catch {
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [token, apiBaseUrl, videoUrl]);
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);
  useEffect(() => {
    if (!status || !["stopped", "compiling"].includes(status.status)) return;
    const interval = setInterval(fetchStatus, 3e3);
    return () => clearInterval(interval);
  }, [status?.status, fetchStatus]);
  const inHold = Boolean(status?.editHoldUntil) && status?.status !== "failed";
  const cardButtonStyle = {
    background: colors.bg.surface,
    border: `1px solid ${colors.border.default}`,
    borderRadius: radii.lg
  };
  return /* @__PURE__ */ jsxs("div", { style: { padding: spacing.lg }, children: [
    /* @__PURE__ */ jsxs("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: spacing.lg }, children: [
      onBack && showBack && /* @__PURE__ */ jsx(Button, { variant: "secondary", size: "sm", onClick: onBack, style: cardButtonStyle, children: "\u2190 Back" }),
      /* @__PURE__ */ jsx("div", { style: { display: "flex", gap: spacing.sm }, children: onArchive && /* @__PURE__ */ jsx(Button, { variant: "secondary", size: "sm", onClick: onArchive, style: cardButtonStyle, children: "Archive" }) })
    ] }),
    error && /* @__PURE__ */ jsx(ErrorDisplay, { error, variant: "banner", title: "Error" }),
    !status && !error && /* @__PURE__ */ jsx(SessionDetailSkeleton, {}),
    status && editing && // Same overlay the recorder uses, so editing looks and behaves
    // identically wherever it's entered from.
    /* @__PURE__ */ jsx(Overlay, { label: "Review your timelapse", height: "min(820px, 92vh)", children: /* @__PURE__ */ jsx("div", { style: { flex: "1 1 auto", minHeight: 0, padding: spacing.xl }, children: /* @__PURE__ */ jsx(
      TimelapseEditor,
      {
        token,
        apiBaseUrl,
        onCancel: () => setEditing(false),
        onApplied: () => {
          setEditing(false);
          setVideoUrl(null);
          fetchStatus();
        }
      }
    ) }) }),
    status && !editing && inHold && /* @__PURE__ */ jsx(
      HoldPanel,
      {
        editable: status.editable === true,
        progress: status.progress,
        client,
        onEdit: () => onEdit ? onEdit() : setEditing(true),
        onPublish: async () => {
          try {
            await fetch(`${apiBaseUrl}/api/sessions/${token}/compile`, {
              method: "POST"
            });
          } catch {
          }
          fetchStatus();
        }
      }
    ),
    status && /* @__PURE__ */ jsxs(Fragment, { children: [
      !inHold && /* @__PURE__ */ jsx("div", { style: { marginBottom: spacing.lg, borderRadius: radii.lg, overflow: "hidden" }, children: /* @__PURE__ */ jsx(
        ProcessingState,
        {
          status: status.status,
          trackedSeconds: status.trackedSeconds,
          videoUrl
        }
      ) }),
      sessionInfo && /* @__PURE__ */ jsxs("div", { style: { marginBottom: spacing.lg }, children: [
        /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: spacing.xs, height: 32 }, children: [
          /* @__PURE__ */ jsxs(
            "form",
            {
              onSubmit: async (e) => {
                e.preventDefault();
                const newName = editName.trim();
                if (newName && newName !== sessionInfo.name) {
                  try {
                    const res = await fetch(`${apiBaseUrl}/api/sessions/${token}/name`, {
                      method: "PATCH",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ name: newName })
                    });
                    if (res.ok) {
                      setSessionInfo((prev) => prev ? { ...prev, name: newName } : prev);
                    } else {
                      alert("Failed to rename session.");
                    }
                  } catch (err) {
                    alert("Error renaming session.");
                  }
                }
                setIsRenaming(false);
                if (inputRef.current) inputRef.current.blur();
              },
              style: {
                display: "grid",
                alignItems: "center",
                margin: 0
              },
              children: [
                /* @__PURE__ */ jsx(
                  motion.span,
                  {
                    animate: {
                      padding: isRenaming ? "0 16px" : "0 8px 0 0"
                    },
                    transition: { type: "spring", stiffness: 500, damping: 30 },
                    style: {
                      gridArea: "1 / 1",
                      visibility: "hidden",
                      whiteSpace: "pre",
                      fontFamily: "inherit",
                      fontSize: fontSize.xl,
                      fontWeight: fontWeight.bold,
                      pointerEvents: "none"
                    },
                    children: isRenaming ? editName || " " : sessionInfo.name
                  }
                ),
                /* @__PURE__ */ jsx(
                  motion.input,
                  {
                    ref: inputRef,
                    readOnly: !isRenaming,
                    value: isRenaming ? editName : sessionInfo.name,
                    onChange: (e) => setEditName(e.target.value),
                    onBlur: () => setIsRenaming(false),
                    onDoubleClick: () => {
                      if (!isRenaming) {
                        setEditName(sessionInfo.name);
                        setIsRenaming(true);
                      }
                    },
                    onKeyDown: (e) => {
                      if (e.key === "Escape") {
                        setIsRenaming(false);
                        e.currentTarget.blur();
                      }
                    },
                    size: 1,
                    animate: {
                      padding: isRenaming ? "0 8px" : "0",
                      width: isRenaming ? "max(100%, 300px)" : "100%",
                      backgroundColor: isRenaming ? colors.bg.surface : "transparent",
                      borderColor: isRenaming ? colors.border.selected : "transparent"
                    },
                    transition: {
                      padding: { type: "spring", stiffness: 500, damping: 30 },
                      width: { type: "spring", stiffness: 500, damping: 30 },
                      backgroundColor: { duration: 0.15 },
                      borderColor: { duration: 0.15 }
                    },
                    style: {
                      gridArea: "1 / 1",
                      minWidth: 0,
                      height: 32,
                      fontFamily: "inherit",
                      fontSize: fontSize.xl,
                      fontWeight: fontWeight.bold,
                      color: colors.text.primary,
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderRadius: radii.md,
                      boxSizing: "border-box",
                      outline: "none",
                      cursor: isRenaming ? "text" : "default",
                      transformOrigin: "left center"
                    }
                  }
                )
              ]
            }
          ),
          /* @__PURE__ */ jsx("div", { style: { display: "flex", alignItems: "center", width: 24, height: 24 }, children: /* @__PURE__ */ jsx(AnimatePresence, { children: !isRenamingAnim && /* @__PURE__ */ jsx(
            motion.button,
            {
              initial: { opacity: 0, scale: 0.8 },
              animate: { opacity: 1, scale: 1 },
              exit: { opacity: 0, scale: 0.8 },
              transition: { duration: 0.15 },
              title: "Rename session",
              onClick: () => {
                setEditName(sessionInfo.name);
                setIsRenaming(true);
              },
              onMouseDown: (e) => e.currentTarget.style.transform = "scale(0.9)",
              onMouseUp: (e) => e.currentTarget.style.transform = "none",
              onMouseLeave: (e) => e.currentTarget.style.transform = "none",
              style: {
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                margin: -5,
                color: colors.text.tertiary,
                display: "flex",
                alignItems: "center",
                transition: "transform 0.1s ease-in-out"
              },
              children: /* @__PURE__ */ jsx("svg", { width: "12", height: "12", viewBox: "0 0 24 24", fill: "currentColor", children: /* @__PURE__ */ jsx("path", { d: "M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" }) })
            }
          ) }) })
        ] }),
        /* @__PURE__ */ jsx(
          motion.div,
          {
            animate: {
              y: isRenaming == false ? -4 : 0
            },
            transition: { type: "spring", stiffness: 500, damping: 30 },
            style: { fontSize: fontSize.xs, color: colors.text.tertiary, marginTop: 2, y: -4 },
            children: new Date(sessionInfo.createdAt).toLocaleDateString(void 0, { month: "short", day: "numeric", year: "numeric" })
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("div", { style: { display: "flex", gap: spacing.lg, justifyContent: "center" }, children: [
        /* @__PURE__ */ jsxs(Card, { padding: `${spacing.md}px ${spacing.xxl}px`, style: { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: spacing.xs }, children: [
          /* @__PURE__ */ jsx("span", { style: { fontSize: fontSize.xxl, fontWeight: fontWeight.bold, color: colors.text.primary, height: 32, display: "flex", alignItems: "center" }, children: formatTrackedTime(status.trackedSeconds) }),
          /* @__PURE__ */ jsx("span", { style: { fontSize: fontSize.xs, color: colors.text.tertiary }, children: "Tracked time" })
        ] }),
        /* @__PURE__ */ jsxs(Card, { padding: `${spacing.md}px ${spacing.xxl}px`, style: { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: spacing.xs }, children: [
          /* @__PURE__ */ jsx("div", { style: { height: 32, display: "flex", alignItems: "center" }, children: /* @__PURE__ */ jsx(Badge, { status: status.status, variant: "inline", size: "lg" }) }),
          /* @__PURE__ */ jsx("span", { style: { fontSize: fontSize.xs, color: colors.text.tertiary }, children: "Status" })
        ] })
      ] })
    ] })
  ] });
}
var STORAGE_KEY = "lookout-tokens";
function readStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch {
    return [];
  }
}
function writeStore(entries) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
}
function useTokenStore() {
  const [entries, setEntries] = useState(readStore);
  useEffect(() => {
    const handler = (e) => {
      if (e.key === STORAGE_KEY) {
        setEntries(readStore());
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);
  const persist = useCallback((next) => {
    setEntries(next);
    writeStore(next);
  }, []);
  const addToken = useCallback(
    (token, label) => {
      const current = readStore();
      if (current.some((e) => e.token === token)) return;
      persist([
        ...current,
        { token, addedAt: (/* @__PURE__ */ new Date()).toISOString(), label, archived: false }
      ]);
    },
    [persist]
  );
  const archiveToken = useCallback(
    (token) => {
      const current = readStore();
      persist(
        current.map(
          (e) => e.token === token ? { ...e, archived: true } : e
        )
      );
    },
    [persist]
  );
  const unarchiveToken = useCallback(
    (token) => {
      const current = readStore();
      persist(
        current.map(
          (e) => e.token === token ? { ...e, archived: false } : e
        )
      );
    },
    [persist]
  );
  const removeToken = useCallback(
    (token) => {
      const current = readStore();
      persist(current.filter((e) => e.token !== token));
    },
    [persist]
  );
  const tokens = entries.filter((e) => !e.archived);
  const archivedTokens = entries.filter((e) => e.archived);
  const getAllTokenValues = useCallback(
    () => tokens.map((e) => e.token),
    [tokens]
  );
  const hasToken = useCallback(
    (token) => entries.some((e) => e.token === token),
    [entries]
  );
  return {
    tokens,
    archivedTokens,
    addToken,
    archiveToken,
    unarchiveToken,
    removeToken,
    getAllTokenValues,
    hasToken
  };
}
var CACHE_STORAGE_KEY = "lookout:gallery-cache:v2";
var CACHE_MAX_ENTRIES = 500;
function loadPersistedCache() {
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(CACHE_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}
function persistCache(cache) {
  if (typeof localStorage === "undefined") return;
  try {
    const entries = Object.entries(cache).sort(([, a], [, b]) => b.fetchedAt - a.fetchedAt).slice(0, CACHE_MAX_ENTRIES);
    localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
  }
}
var globalSessionsCache = loadPersistedCache();
function useGallery({ apiBaseUrl, tokens }) {
  const validTokens = tokens.filter((t) => /^[a-f0-9]{64}$/i.test(t));
  const initialSessions = validTokens.map((t) => globalSessionsCache[t]?.summary).filter((s) => s !== void 0);
  const hasAllInCache = validTokens.length > 0 && initialSessions.length === validTokens.length;
  const [sessions, setSessions] = useState(initialSessions);
  const [loading, setLoading] = useState(!hasAllInCache && validTokens.length > 0);
  const [error, setError] = useState(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const tokensKey = tokens.join(",");
  const refresh = useCallback(() => setRefreshCounter((c) => c + 1), []);
  useEffect(() => {
    if (tokens.length === 0) {
      setSessions([]);
      setLoading(false);
      setError(null);
      return;
    }
    if (validTokens.length === 0) {
      setSessions([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    if (!hasAllInCache) {
      setLoading(true);
    }
    const BATCH_SIZE = 100;
    const chunks = [];
    for (let i = 0; i < validTokens.length; i += BATCH_SIZE) {
      chunks.push(validTokens.slice(i, i + BATCH_SIZE));
    }
    Promise.all(
      chunks.map(
        (chunk) => fetch(`${apiBaseUrl}/api/sessions/batch`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tokens: chunk })
        }).then(async (res) => {
          if (!res.ok) {
            const text = await res.text().catch(() => "");
            throw new Error(`HTTP ${res.status} ${res.statusText}
${text.slice(0, 500)}`);
          }
          return res.json();
        })
      )
    ).then((results) => ({ sessions: results.flatMap((r) => r.sessions ?? []) })).then((data) => {
      if (!cancelled) {
        const now = Date.now();
        const newSessions = data.sessions ?? [];
        for (const session of newSessions) {
          globalSessionsCache[session.token] = { summary: session, fetchedAt: now };
        }
        persistCache(globalSessionsCache);
        setSessions(newSessions);
        setError(null);
      }
    }).catch((err) => {
      if (!cancelled) {
        console.warn("Gallery fetch error:", err instanceof Error ? err.message : err);
        setError(err.message);
      }
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, tokensKey, refreshCounter, hasAllInCache]);
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [refresh]);
  return { sessions, loading, error, refresh };
}
function parseHash(hash) {
  const cleaned = hash.replace(/^#\/?/, "");
  if (!cleaned || cleaned === "/") return { page: "gallery" };
  const [path, queryStr] = cleaned.split("?");
  const params = new URLSearchParams(queryStr ?? "");
  const token = params.get("token") ?? "";
  if (path === "add") return { page: "add" };
  if (path === "settings") return { page: "settings" };
  if (path === "tray") return { page: "tray" };
  if (path === "record" && token) return { page: "record", token };
  if (path === "session" && token) return { page: "session", token };
  if (path === "editor" && token) return { page: "editor", token };
  return { page: "gallery" };
}
function routeToHash(route) {
  switch (route.page) {
    case "gallery":
      return "#/";
    case "add":
      return "#/add";
    case "settings":
      return "#/settings";
    case "tray":
      return "#/tray";
    case "record":
      return `#/record?token=${route.token}`;
    case "session":
      return `#/session?token=${route.token}`;
    case "editor":
      return `#/editor?token=${route.token}`;
  }
}
function useHashRouter() {
  const [route, setRoute] = useState(() => parseHash(window.location.hash));
  useEffect(() => {
    const handler = () => setRoute(parseHash(window.location.hash));
    window.addEventListener("hashchange", handler);
    return () => window.removeEventListener("hashchange", handler);
  }, []);
  useEffect(() => {
    const keyHandler = (e) => {
      const target = e.target;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
        return;
      }
      if (e.defaultPrevented) return;
      if (e.key === "Escape" || e.key === "Backspace") {
        const currentRoute = parseHash(window.location.hash);
        if (currentRoute.page !== "gallery") {
          e.preventDefault();
          window.history.back();
          setTimeout(() => {
            if (parseHash(window.location.hash).page !== "gallery") {
              window.location.hash = "#/";
            }
          }, 50);
        }
      }
    };
    window.addEventListener("keydown", keyHandler);
    return () => window.removeEventListener("keydown", keyHandler);
  }, []);
  const navigate = useCallback((r) => {
    window.location.hash = routeToHash(r);
  }, []);
  return { route, navigate };
}

export { Badge, Button, CameraPreview, CameraSelector, Card, ClipRecorder, ErrorDisplay, Gallery, GallerySkeleton, LookoutProvider, LookoutRecorder, MAX_INTERPOLATION_S, MinutesFlow, Overlay, PageContainer, ProcessingState, ProgressRing, RecordPageSkeleton, RecordingControls, ResultView, ScreenPreview, SessionCard, SessionDetail, SessionDetailSkeleton, Skeleton, Spinner, StatusBar, StopChoiceModal, TimelapseEditor, VideoPlayer, colors, computeBestTrackedSeconds, createLookoutClient, cutsToRegions, deriveDisplaySeconds, drawScaledHQ, fontSize, fontWeight, formatTime, formatTrackedTime, gapIndices, normalizeRegions, radii, regionsToCuts, setAccentColor, spacing, statusConfig, useCameraCapture, useEditLease, useGallery, useHashRouter, useLookout, useScreenCapture, useSession, useSessionTimer, useSessionTimerState, useTokenStore, useUploader };
